import React, {useState, useEffect, useRef, useCallback, createContext, useContext} from 'react';
import jsQR from 'jsqr';
import QRCode from 'qrcode';
import {Routes, Route, useNavigate, useParams, useLocation, Navigate} from 'react-router-dom';
import './App.css';

// ══════════════════════════════════
//  SUPABASE CONFIG
// ══════════════════════════════════
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const PHOTO_BUCKET = 'wedding-photos';
const STORAGE_BASE_URL = `${SUPABASE_URL}/storage/v1/object/${PHOTO_BUCKET}`;

// ══════════════════════════════════
//  VIDEO CACHE (Cache API)
// ══════════════════════════════════
const HERO_VIDEO_URL = 'https://krswaklzkmmlarzcsylh.supabase.co/storage/v1/object/public/assets/hero-video.mp4';
const VIDEO_CACHE_NAME = 'promessa-video-v1';

// Pre-fetches the video into the browser Cache API on first visit,
// so subsequent visits serve it instantly from disk (no network).
async function preCacheVideo() {
    if (!('caches' in window)) return;
    try {
        const cache = await caches.open(VIDEO_CACHE_NAME);
        const existing = await cache.match(HERO_VIDEO_URL);
        if (existing) return; // already cached — nothing to do
        await cache.add(HERO_VIDEO_URL);
    } catch (e) {
        // Non-blocking — silently ignore errors (quota exceeded, network down, etc.)
    }
}

// Returns the cached blob URL if available, otherwise the original URL.
async function getCachedVideoUrl() {
    if (!('caches' in window)) return HERO_VIDEO_URL;
    try {
        const cache = await caches.open(VIDEO_CACHE_NAME);
        const response = await cache.match(HERO_VIDEO_URL);
        if (response) {
            const blob = await response.blob();
            return URL.createObjectURL(blob);
        }
    } catch (e) {
        // Fall through to original URL
    }
    return HERO_VIDEO_URL;
}

// React hook — resolves the best available video src on mount.
function useHeroVideoSrc() {
    const [src, setSrc] = useState(HERO_VIDEO_URL);
    useEffect(() => {
        let objectUrl = null;
        getCachedVideoUrl().then(url => {
            objectUrl = url !== HERO_VIDEO_URL ? url : null;
            setSrc(url);
        });
        preCacheVideo(); // warm the cache for next visit
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl); // cleanup blob URL on unmount
        };
    }, []);
    return src;
}

async function supaFetch(path, options = {}) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            Prefer: options.prefer || '',
            ...options.headers,
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
    }
    if (res.status === 204) return null;
    return res.json();
}

// ── Storage ───────────────────────────────────────────────────────────────────

async function storageUploadPhoto(file, weddingId) {
    const fileName = `photo_${weddingId}.jpg`;
    const res = await fetch(`${STORAGE_BASE_URL}/${fileName}`, {
        method: 'POST',
        headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': file.type || 'image/jpeg',
            'x-upsert': 'true',
        },
        body: file,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Storage upload HTTP ${res.status}`);
    }
    return `${SUPABASE_URL}/storage/v1/object/public/${PHOTO_BUCKET}/${fileName}`;
}

// ── DB helpers ────────────────────────────────────────────────────────────────
async function dbGetWeddingById(id) {
    const rows = await supaFetch(`wedding?id=eq.${encodeURIComponent(id)}&select=*&limit=1`);
    return rows?.[0] || null;
}
async function dbUpsertWedding(weddingId, data) {
    return supaFetch('wedding?on_conflict=id', {
        method: 'POST',
        prefer: 'resolution=merge-duplicates,return=representation',
        body: JSON.stringify({id: weddingId, ...data}),
    });
}
async function dbPatchWedding(weddingId, data) {
    return supaFetch(`wedding?id=eq.${weddingId}`, {
        method: 'PATCH',
        prefer: 'return=representation',
        body: JSON.stringify(data),
    });
}
async function dbGetGuests(weddingId) {
    return supaFetch(`guests?wedding_id=eq.${weddingId}&select=*&order=added_at.asc`) || [];
}
async function dbInsertGuest(guest) {
    return supaFetch('guests', {
        method: 'POST',
        prefer: 'return=representation',
        body: JSON.stringify({
            id: guest.id,
            wedding_id: guest.weddingId,
            name: guest.name,
            phone: guest.phone || null,
            note: guest.note || null,
            status: guest.status,
            companions: guest.companions || 0,
            message: guest.message || null,
            table_number: guest.tableNumber || null,
            added_at: guest.addedAt,
            replied_at: guest.repliedAt || null,
        }),
    });
}
async function dbUpdateGuest(id, fields) {
    return supaFetch(`guests?id=eq.${id}`, {
        method: 'PATCH',
        prefer: 'return=representation',
        body: JSON.stringify(fields),
    });
}
async function dbDeleteGuest(id) {
    return supaFetch(`guests?id=eq.${id}`, {method: 'DELETE'});
}
async function dbCheckInGuest(id, checkedIn) {
    return supaFetch(`guests?id=eq.${id}`, {
        method: 'PATCH',
        prefer: 'return=representation',
        body: JSON.stringify({
            checked_in: checkedIn,
            checked_in_at: checkedIn ? new Date().toISOString() : null,
        }),
    });
}
function rowToGuest(r) {
    return {
        id: r.id, name: r.name, phone: r.phone || '',
        note: r.note || '', status: r.status,
        companions: r.companions || 0, message: r.message || '',
        tableNumber: r.table_number || '',
        addedAt: r.added_at, repliedAt: r.replied_at,
        checkedIn: r.checked_in || false,
        checkedInAt: r.checked_in_at || null,
    };
}

function getPhotoUrl(w) {
    if (w.photo_url) return w.photo_url + '?t=' + Date.now();
    return `${STORAGE_BASE_URL}/photo_${w.id}.jpg` + '?t=' + Date.now();
}

// ══════════════════════════════════
//  UTILS
// ══════════════════════════════════
function formatDate(d) {
    if (!d) return '';
    const months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
    const parts = d.split('-');
    return `${parseInt(parts[2])} ${months[parseInt(parts[1])-1]} ${parts[0]}`;
}
function escHtml(str) {
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}
function StatusBadge({status, checkedIn}) {
    if (checkedIn) return <span className="badge" style={{background:'#c8e6c9', color:'#2e7d32', letterSpacing:1}}>Présent</span>;
    if (status === 'confirmed') return <span className="badge badge-yes">Confirmé</span>;
    if (status === 'declined')  return <span className="badge badge-no">Décliné</span>;
    return <span className="badge badge-wait">Attente</span>;
}
function Toast({message, type}) {
    const bg = type === 'error' ? '#c0392b' : type === 'warn' ? '#b8985a' : '#2c2420';
    return <div className={`toast ${message ? 'show' : ''}`} style={{background: bg}}>{message}</div>;
}
// ── Guest QR Code component ──────────────────────────────────────────────────
// Generates a QR from the guest's personal invite URL (same URL as WhatsApp)
// and renders it as an <img> via canvas data URL.
function GuestQRCode({ url, size = 200 }) {
    const [src, setSrc] = useState('');
    useEffect(() => {
        if (!url) return;
        QRCode.toDataURL(url, {
            width: size,
            margin: 2,
            color: { dark: '#2c2420', light: '#faf7f2' },
            errorCorrectionLevel: 'M',
        }).then(setSrc).catch(() => {});
    }, [url, size]);
    if (!src) return <div style={{width:size, height:size, background:'var(--cream)', display:'flex', alignItems:'center', justifyContent:'center'}}><span style={{color:'var(--mid)', fontSize:12}}>…</span></div>;
    return <img src={src} alt="QR Code" width={size} height={size} style={{display:'block', borderRadius:4}} />;
}

function CameraIcon({ size = 18, style = {} }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
             xmlns="http://www.w3.org/2000/svg"
             style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}>
            <rect x="2" y="7" width="20" height="14" rx="3" fill="currentColor" opacity="0.15"/>
            <rect x="2" y="7" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="1.6"/>
            <path d="M8 7V5.5C8 4.67 8.67 4 9.5 4h5c.83 0 1.5.67 1.5 1.5V7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <circle cx="12" cy="14" r="3.2" stroke="currentColor" strokeWidth="1.6"/>
            <circle cx="12" cy="14" r="1.3" fill="currentColor" opacity="0.6"/>
            <circle cx="18.5" cy="10.5" r="1" fill="currentColor" opacity="0.7"/>
        </svg>
    );
}

function Modal({id, open, onClose, children}) {
    return (
        <div className={`modal-overlay ${open ? 'open' : ''}`} id={id}
             onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            {children}
        </div>
    );
}
function LoadingScreen() {
    return (
        <div style={{minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16}}>
            <div className="ornament" style={{fontSize:32}}>✦</div>
            <p className="serif" style={{fontSize:20, fontStyle:'italic', color:'var(--rose-dark)'}}>Chargement…</p>
        </div>
    );
}
function buildInvitationHTML(w, photo, mini, rsvpUrl, hidePhoto=false, photoPosition=null) {
    if (!w) return '';
    const dateStr = w.date ? formatDate(w.date) : 'Date à confirmer';
    const timeStr = w.time ? w.time.replace(':','h') : '';
    const pos = photoPosition || (w.photo_position ? w.photo_position : {x:50, y:50});
    const objPos = `${pos.x}% ${pos.y}%`;
    return `
    <div class="invitation-preview" style="${mini?'transform:scale(0.8);transform-origin:top left;width:125%;':''}">
      ${hidePhoto ? '' : (photo
        ? `<div class="inv-photo-wrap"><img src="${photo}" class="inv-photo" style="object-position:${objPos}" alt="Photo des époux"/><div class="inv-photo-overlay"></div></div>`
        : `<div class="inv-photo-wrap" style="display:flex;align-items:center;justify-content:center;"><span style="font-size:60px;color:var(--rose);opacity:0.4;">♥</span></div>`)}
      <div class="inv-body">
        <div class="ornament" style="font-size:16px;letter-spacing:5px;">✦ ✦ ✦</div>
        <p style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:var(--gold);margin-top:12px;">Vous êtes cordialement invité(e) à célébrer</p>
        <div class="inv-names">${escHtml(w.name1)}<span class="inv-et">&</span>${escHtml(w.name2)}</div>
        <div class="inv-date-block">
          <span class="inv-date-big">${escHtml(dateStr)}</span>
          ${timeStr ? `<p style="font-size:13px;color:var(--mid);margin-top:6px;">${escHtml(timeStr)}</p>` : ''}
        </div>
        ${w.venue ? `<div class="inv-detail-row"><span class="inv-detail-icon">◈</span><span><strong>${escHtml(w.venue)}</strong>${w.address?'<br>'+escHtml(w.address):''}</span></div>` : ''}
        ${w.reception ? `<div class="inv-detail-row"><span class="inv-detail-icon">◈</span><span>Réception : ${escHtml(w.reception)}</span></div>` : ''}
        ${w.message ? `<div class="ornament" style="font-size:14px;margin:16px 0 8px;">✦</div><p class="inv-message">${escHtml(w.message)}</p>` : ''}
        <div class="inv-rsvp-block">
          <p style="font-size:12px;letter-spacing:1.5px;text-transform:uppercase;color:var(--rose-dark);font-weight:500;margin-bottom:8px;">Confirmez votre présence</p>
          ${w.rsvpDate ? `<p style="font-size:12px;color:var(--mid);margin-bottom:12px;">Avant le ${formatDate(w.rsvpDate)}</p>` : ''}
          ${!mini
        ? `<button class="btn btn-primary open-rsvp-btn" style="width:100%;">Répondre à l'invitation</button>`
        : `<p style="font-size:11px;color:var(--mid);">${escHtml(rsvpUrl)}</p>`}
          ${w.rsvpContact && !mini ? `<p style="font-size:11px;color:var(--mid);margin-top:10px;">Contact : ${escHtml(w.rsvpContact)}</p>` : ''}
        </div>
        <div class="ornament" style="font-size:14px;margin-top:20px;">✦ ✦ ✦</div>
      </div>
    </div>`;
}

// ══════════════════════════════════
//  CONTEXT
// ══════════════════════════════════
const AppContext = createContext(null);
function useApp() { return useContext(AppContext); }

// ══════════════════════════════════
//  NAV
// ══════════════════════════════════
// ── Mobile hook ───────────────────────────────────────────────────────────────
function useIsMobile() {
    const [mobile, setMobile] = useState(() => window.innerWidth <= 700);
    useEffect(() => {
        const fn = () => setMobile(window.innerWidth <= 700);
        window.addEventListener('resize', fn);
        return () => window.removeEventListener('resize', fn);
    }, []);
    return mobile;
}

function Nav() {
    const {isLoggedIn, wedding, logout} = useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile();
    const [menuOpen, setMenuOpen] = useState(false);
    const weddingId = wedding?.id;
    const onDashboard = location.pathname.endsWith('/dashboard');
    const onSettings  = location.pathname.endsWith('/settings');
    const onCheckin   = location.pathname.endsWith('/checkin');

    return (
        <nav id="mainNav" style={{display:'flex', position:'relative'}}>
            <div className="nav-logo" onClick={() => navigate('/')} style={{cursor:'pointer', display:'flex', alignItems:'center'}}>
                <img src="/couple.png" alt=""
                     style={{height:32, width:'auto', marginRight:6, //filter:'brightness(0) invert(1) opacity(0.9)'
                }} />
                <span style={{fontFamily:"'Cormorant Garamond',Georgia,serif", fontStyle:'italic', fontWeight:300, fontSize:22, letterSpacing:1}}>Promessa</span>
            </div>

            {isMobile ? (
                <>
                    <button onClick={() => setMenuOpen(v => !v)}
                            style={{background:'none', border:'none', cursor:'pointer', padding:'8px', marginLeft:'auto', color:'var(--mid)', fontSize:20, lineHeight:1}}>
                        {menuOpen ? '✕' : '☰'}
                    </button>
                    {menuOpen && (
                        <div style={{position:'absolute', top:'100%', left:0, right:0, background:'white', borderBottom:'1px solid var(--border)', zIndex:100, display:'flex', flexDirection:'column', padding:'8px 0'}}>
                            {isLoggedIn && weddingId ? (
                                <>
                                    <button style={{textAlign:'left', padding:'12px 20px', background:'none', border:'none', fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1.5, textTransform:'uppercase', color: onDashboard ? 'var(--rose)' : 'var(--mid)', cursor:'pointer'}}
                                            onClick={() => { navigate(`/${weddingId}/dashboard`); setMenuOpen(false); }}>Tableau de bord</button>
                                    <button style={{textAlign:'left', padding:'12px 20px', background:'none', border:'none', fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1.5, textTransform:'uppercase', color: onSettings ? 'var(--rose)' : 'var(--mid)', cursor:'pointer'}}
                                            onClick={() => { navigate(`/${weddingId}/settings`); setMenuOpen(false); }}>Modifier l'invitation</button>
                                    <button style={{textAlign:'left', padding:'12px 20px', background:'none', border:'none', fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1.5, textTransform:'uppercase', color: onCheckin ? 'var(--rose)' : 'var(--mid)', cursor:'pointer'}}
                                            onClick={() => { navigate(`/${weddingId}/checkin`); setMenuOpen(false); }}><CameraIcon style={{marginRight:6}}/> Contrôle accès</button>
                                    <button style={{textAlign:'left', padding:'12px 20px', background:'none', border:'none', fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1.5, textTransform:'uppercase', color:'var(--mid)', cursor:'pointer'}}
                                            onClick={() => { logout(); setMenuOpen(false); }}>Déconnexion</button>
                                </>
                            ) : (
                                <button style={{textAlign:'left', padding:'12px 20px', background:'none', border:'none', fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1.5, textTransform:'uppercase', color:'var(--mid)', cursor:'pointer'}}
                                        onClick={() => { navigate('/login'); setMenuOpen(false); }}>Espace époux</button>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <div className="nav-links">
                    {isLoggedIn && weddingId ? (
                        <>
                            <button className={onDashboard ? 'active' : ''} onClick={() => navigate(`/${weddingId}/dashboard`)}>Tableau de bord</button>
                            <button className={onSettings ? 'active' : ''} onClick={() => navigate(`/${weddingId}/settings`)}>Modifier l'invitation</button>
                            <button className={onCheckin ? 'active' : ''} onClick={() => navigate(`/${weddingId}/checkin`)}><CameraIcon style={{marginRight:4}}/> Accès</button>
                            <button onClick={logout}>Déconnexion</button>
                        </>
                    ) : (
                        <button onClick={() => navigate('/login')}>Espace époux</button>
                    )}
                </div>
            )}
        </nav>
    );
}

// ══════════════════════════════════
//  APP ROOT
// ══════════════════════════════════
const SESSION_KEY = 'promessa_session';

export default function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [wedding, setWedding]       = useState(null);
    const [guests, setGuests]         = useState([]);
    const [photoData, setPhotoData]   = useState(null);
    const [photoPosition, setPhotoPosition] = useState({x:50, y:50});
    const [toast, setToast]           = useState({msg:'', type:'ok'});
    const [sessionLoading, setSessionLoading] = useState(true);
    const toastTimer = useRef(null);
    const navigate = useNavigate();

    const showToast = useCallback((msg, type='ok') => {
        setToast({msg, type});
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast({msg:'', type:'ok'}), 3200);
    }, []);


    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;
        navigator.serviceWorker.register('./service-worker.js', {scope: './'})
            .then(reg => {
                console.log('[Promessa] SW registrato:', reg.scope);

                // Quando il browser trova un nuovo SW, lo installa in background.
                // skipWaiting() nel SW fa sì che si attivi subito.
                // Qui ascoltiamo l'evento e ricarichiamo la pagina automaticamente.
                reg.addEventListener('updatefound', () => {
                    const newWorker = reg.installing;
                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'activated') {
                            console.log('[Promessa] Nuovo SW attivato — ricarico la pagina');
                            window.location.reload();
                        }
                    });
                });
            })
            .catch(err => console.warn('[Promessa] SW non registrato:', err));

        // Ascolta il messaggio SW_UPDATED inviato dal nuovo SW dopo activate
        navigator.serviceWorker.addEventListener('message', event => {
            if (event.data?.type === 'SW_UPDATED') {
                console.log('[Promessa] SW aggiornato — ricarico');
                window.location.reload();
            }
        });
    }, []);

    // Restore session on mount
    useEffect(() => {
        async function restoreSession() {
            try {
                const raw = sessionStorage.getItem(SESSION_KEY);
                if (raw) {
                    const {username, password} = JSON.parse(raw);
                    const normalizedUsername = username.trim().toLowerCase();
                    const normalizedPassword = password.trim().toLowerCase();
                    const rows = await supaFetch(`wedding?username=eq.${encodeURIComponent(normalizedUsername)}&select=*&limit=1`);
                    const w = rows?.[0] || null;
                    if (w && w.password.trim().toLowerCase() === normalizedPassword) {
                        const gs = await dbGetGuests(w.id);
                        setWedding(w);
                        setGuests(gs.map(rowToGuest));
                        setPhotoData(getPhotoUrl(w));
                        if (w.photo_position) setPhotoPosition(w.photo_position);
                        setIsLoggedIn(true);
                    } else {
                        sessionStorage.removeItem(SESSION_KEY);
                    }
                }
            } catch(e) {
                sessionStorage.removeItem(SESSION_KEY);
            } finally {
                setSessionLoading(false);
            }
        }
        restoreSession();
        // eslint-disable-next-line
    }, []);

    const login = useCallback(async (username, password) => {
        const normalizedUsername = username.trim().toLowerCase();
        const normalizedPassword = password.trim().toLowerCase();
        const rows = await supaFetch(`wedding?username=eq.${encodeURIComponent(normalizedUsername)}&select=*&limit=1`);
        const w = rows?.[0] || null;
        if (!w || w.password.trim().toLowerCase() !== normalizedPassword || !w.active) throw new Error('Identifiant ou mot de passe incorrect.');
        const gs = await dbGetGuests(w.id);
        setWedding(w);
        setGuests(gs.map(rowToGuest));
        setPhotoData(getPhotoUrl(w));
        if (w.photo_position) setPhotoPosition(w.photo_position);
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({username: normalizedUsername, password: normalizedPassword}));
        setIsLoggedIn(true);
        const redirectTo = new URLSearchParams(window.location.search).get('redirect');
        navigate(redirectTo || `/${w.id}/dashboard`);
    }, [navigate]);

    const logout = useCallback(() => {
        setIsLoggedIn(false);
        setWedding(null);
        setGuests([]);
        setPhotoData(null);
        sessionStorage.removeItem(SESSION_KEY);
        navigate('/login');
    }, [navigate]);

    // DOPO — usa PATCH se il wedding esiste già, POST solo per creazione
    const saveWedding = useCallback(async (cfg) => {
        const normalizedCfg = {
            ...cfg,
            username: cfg.username ? cfg.username.trim().toLowerCase() : cfg.username,
            password: cfg.password ? cfg.password.trim().toLowerCase() : cfg.password,
        };

        let saved;
        if (wedding?.id) {
            // UPDATE — usa PATCH
            const rows = await dbPatchWedding(wedding.id, {...normalizedCfg});
            saved = rows?.[0] || {...normalizedCfg, id: wedding.id};
        } else {
            // CREATE — usa upsert POST
            const rows = await dbUpsertWedding(null, {...normalizedCfg});
            saved = rows?.[0] || {...normalizedCfg};
        }

        setWedding(saved);
        setIsLoggedIn(true);
        navigate(`/${saved?.id || wedding?.id}/dashboard`);
        return saved;
    }, [navigate, wedding]);

    const uploadPhoto = useCallback(async (file) => {
        const url = await storageUploadPhoto(file, wedding?.id);
        await dbPatchWedding(wedding?.id, {photo_url: url});
        setPhotoData(url + '?t=' + Date.now());
        setWedding(prev => ({...prev, photo_url: url}));
        return url;
    }, [wedding]);

    const savePhotoPosition = useCallback((pos) => {
        setPhotoPosition(pos);
        setWedding(prev => ({...prev, photo_position: pos}));
        // DB save is deferred to savePhotoPositionFinal (called on drag end)
    }, []);

    const savePhotoPositionFinal = useCallback(async (pos) => {
        setPhotoPosition(pos);
        setWedding(prev => ({...prev, photo_position: pos}));
        await dbPatchWedding(wedding?.id, {photo_position: pos});
    }, [wedding]);

    const addGuest = useCallback(async (name, phone, note, tableNumber, isCouple=false) => {
        const exists = guests.find(g => g.name.toLowerCase() === name.toLowerCase());
        if (exists) { showToast('Cet invité est déjà dans la liste.', 'warn'); return false; }
        const newGuest = {
            id: Date.now(), name, phone, note,
            tableNumber: tableNumber || '',
            status: 'pending',
            companions: isCouple ? 1 : 0,
            weddingId: wedding?.id,
            message: '',
            addedAt: new Date().toISOString(),
        };
        await dbInsertGuest(newGuest);
        setGuests(prev => [...prev, newGuest]);
        return true;
    }, [guests, wedding, showToast]);

    const removeGuest = useCallback(async (id) => {
        await dbDeleteGuest(id);
        setGuests(prev => prev.filter(g => g.id !== id));
        showToast('Invité supprimé.');
    }, [showToast]);

    const ctx = {
        isLoggedIn, wedding, guests, photoData, photoPosition,
        login, logout, saveWedding, uploadPhoto, savePhotoPosition, savePhotoPositionFinal,
        addGuest, removeGuest, showToast,
        setGuests, sessionLoading,
    };

    return (
        <AppContext.Provider value={ctx}>
            <Routes>
                <Route path="/"                      element={<HomePage />} />
                <Route path="/login"                 element={<LoginPage />} />
                <Route path="/setup"                 element={<SetupPage />} />
                <Route path="/:weddingId"                 element={<InvitationPage />} />
                <Route path="/:weddingId/dashboard"       element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="/:weddingId/preview"         element={<ProtectedRoute><PreviewPage /></ProtectedRoute>} />
                <Route path="/:weddingId/settings"        element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route path="/:weddingId/checkin"          element={<ProtectedRoute><CheckInPage /></ProtectedRoute>} />
                <Route path="/:weddingId/guests/:guestId" element={<GuestRsvpRedirect />} />
                <Route path="*" element={<FallbackRoute />} />
            </Routes>
            <Toast message={toast.msg} type={toast.type} />
            <PwaInstallBanner />
        </AppContext.Provider>
    );
}


// ══════════════════════════════════
//  PAGE: CHECK-IN  /:weddingId/checkin
// ══════════════════════════════════
function CheckInPage() {
    const { wedding, guests, setGuests, showToast } = useApp();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const scanIntervalRef = useRef(null);
    const lastScannedRef = useRef(null);

    const [mode, setMode] = useState('scanner'); // 'scanner' | 'manual'
    const [scanning, setScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null); // { guest, status: 'ok'|'already'|'notfound' }
    const [manualSearch, setManualSearch] = useState('');
    const [cameraError, setCameraError] = useState(null);

    const checkedInCount = guests.filter(g => g.checkedIn).length;
    const confirmedCount = guests.filter(g => g.status === 'confirmed').length;

    // ── QR code parsing: the QR encodes the guest's RSVP URL  ─────────────────
    // Format: https://promessa.app/:weddingId?gid=GUEST_ID&...
    const processCheckIn = useCallback(async (guestId) => {
        const guest = guests.find(g => String(g.id) === String(guestId));
        if (!guest) { setScanResult({ status: 'notfound', raw: guestId }); return; }
        if (guest.checkedIn) { setScanResult({ status: 'already', guest }); return; }
        try {
            await dbCheckInGuest(guest.id, true);
            setGuests(prev => prev.map(g => g.id === guest.id ? { ...g, checkedIn: true, checkedInAt: new Date().toISOString() } : g));
            setScanResult({ status: 'ok', guest: { ...guest, checkedIn: true } });
        } catch (e) {
            showToast('Erreur check-in : ' + e.message, 'error');
        }
    }, [guests, setGuests, showToast]);

    const handleQRCode = useCallback((data) => {
        try {
            const url = new URL(data);
            const gid = url.searchParams.get('gid');
            if (!gid) { setScanResult({ status: 'notfound', raw: data }); return; }
            processCheckIn(String(gid));
        } catch {
            // Try treating the raw data as a guest ID directly
            processCheckIn(data.trim());
        }
    }, [processCheckIn]);

    // ── QR scanning via jsQR (bundled via npm, no CDN needed) ──────────────────

    const startCamera = useCallback(async () => {
        setCameraError(null);
        setScanning(false);

        // 1. Check browser support
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setCameraError(`Votre navigateur ne supporte pas l'accès à la caméra. Utilisez Chrome ou Safari récent.`);
            return;
        }

        // 2. Request camera — try rear camera first, fallback to any
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' } }
            });
        } catch (e1) {
            try {
                stream = await navigator.mediaDevices.getUserMedia({ video: true });
            } catch (e2) {
                const msg = e2.name === 'NotAllowedError'
                    ? `Permission caméra refusée. Autorisez l'accès dans les paramètres du navigateur.`
                    : e2.name === 'NotFoundError'
                        ? 'Aucune caméra trouvée sur cet appareil.'
                        : 'Caméra non disponible : ' + (e2.message || e2.name);
                setCameraError(msg);
                return;
            }
        }

        // 4. Attach stream to video element
        const video = videoRef.current;
        if (!video) { stream.getTracks().forEach(t => t.stop()); return; }
        video.srcObject = stream;
        try { await video.play(); } catch(e) { /* autoplay may be blocked briefly */ }
        setScanning(true);

        // 5. Scan loop
        scanIntervalRef.current = setInterval(() => {
            const vid = videoRef.current;
            const canvas = canvasRef.current;
            if (!vid || !canvas || vid.readyState < 2 || vid.videoWidth === 0) return;
            canvas.width  = vid.videoWidth;
            canvas.height = vid.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(vid, 0, 0, canvas.width, canvas.height);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' });
            if (code) {
                const now = Date.now();
                if (lastScannedRef.current === code.data && now - (lastScannedRef.lastTime || 0) < 3000) return;
                lastScannedRef.current = code.data;
                lastScannedRef.lastTime = now;
                handleQRCode(code.data);
            }
        }, 250);
    }, [guests, handleQRCode]);

    const stopCamera = useCallback(() => {
        clearInterval(scanIntervalRef.current);
        if (videoRef.current?.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(t => t.stop());
            videoRef.current.srcObject = null;
        }
        setScanning(false);
    }, []);

    useEffect(() => {
        if (mode === 'scanner') startCamera();
        return () => stopCamera();
    }, [mode]);

    // Stop camera whenever the component unmounts (back button, navigate, etc.)
    useEffect(() => {
        return () => stopCamera();
    }, []);

    const handleManualCheckIn = useCallback(async (guest) => {
        if (guest.checkedIn) { showToast(guest.name + ' est déjà enregistré.', 'warn'); return; }
        try {
            await dbCheckInGuest(guest.id, true);
            setGuests(prev => prev.map(g => g.id === guest.id ? { ...g, checkedIn: true, checkedInAt: new Date().toISOString() } : g));
            showToast('✓ ' + guest.name + ' enregistré !');
        } catch (e) {
            showToast('Erreur : ' + e.message, 'error');
        }
    }, [guests, setGuests, showToast]);

    const handleUndoCheckIn = useCallback(async (guest) => {
        try {
            await dbCheckInGuest(guest.id, false);
            setGuests(prev => prev.map(g => g.id === guest.id ? { ...g, checkedIn: false, checkedInAt: null } : g));
            showToast(guest.name + ' : présence annulée.', 'warn');
            setScanResult(null);
        } catch (e) {
            showToast('Erreur : ' + e.message, 'error');
        }
    }, [setGuests, showToast]);

    const filteredGuests = guests.filter(g =>
        !manualSearch || g.name.toLowerCase().includes(manualSearch.toLowerCase()) ||
        (g.phone && g.phone.includes(manualSearch))
    );

    return (
        <>
            <Nav />
            <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>
                {/* Header */}
                <div className="hero" style={{ padding: '40px 24px 32px' }}>
                    <div className="ornament">✦ ✦ ✦</div>
                    <h1 className="hero-title" style={{ fontSize: 32 }}>Contrôle d'accès</h1>
                    <p className="hero-sub">{wedding?.name1} & {wedding?.name2}</p>
                    <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginTop: 20, position: 'relative' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 28, fontFamily: "'Cormorant Garamond',serif", fontWeight: 300, color: 'white' }}>{checkedInCount}</div>
                            <div style={{ fontSize: 10, letterSpacing: 2, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>Présents</div>
                        </div>
                        <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 28, fontFamily: "'Cormorant Garamond',serif", fontWeight: 300, color: 'white' }}>{confirmedCount}</div>
                            <div style={{ fontSize: 10, letterSpacing: 2, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>Confirmés</div>
                        </div>
                        <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 28, fontFamily: "'Cormorant Garamond',serif", fontWeight: 300, color: 'white' }}>{guests.length}</div>
                            <div style={{ fontSize: 10, letterSpacing: 2, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>Total</div>
                        </div>
                    </div>
                </div>

                <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 20px' }}>

                    {/* Mode tabs */}
                    <div style={{ display: 'flex', gap: 0, marginBottom: 28, border: '1px solid var(--border)', overflow: 'hidden' }}>
                        {[['scanner', 'Scanner QR', true], ['manual', '🔍 Recherche manuelle', false]].map(([m, label, isCamera]) => (
                            <button key={m} onClick={() => setMode(m)}
                                    style={{
                                        flex: 1, padding: '12px', fontFamily: "'Jost',sans-serif",
                                        fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', cursor: 'pointer',
                                        border: 'none', borderRight: m === 'scanner' ? '1px solid var(--border)' : 'none',
                                        background: mode === m ? 'var(--rose)' : 'white',
                                        color: mode === m ? 'white' : 'var(--mid)',
                                        transition: 'all 0.2s',
                                    }}>
                                {isCamera ? <><CameraIcon size={14} style={{marginRight:5, verticalAlign:'middle'}}/>{label}</> : label}
                            </button>
                        ))}
                    </div>

                    {/* ── SCANNER MODE ─────────────────────────────────────── */}
                    {mode === 'scanner' && (
                        <div>
                            {/* Result banner */}
                            {scanResult && (
                                <div style={{
                                    marginBottom: 20, padding: '20px 24px', border: '1px solid',
                                    borderColor: scanResult.status === 'ok' ? '#7a9b7a' : scanResult.status === 'already' ? 'var(--gold)' : '#c0392b',
                                    background: scanResult.status === 'ok' ? '#e8f0e8' : scanResult.status === 'already' ? 'var(--gold-light)' : '#fdecea',
                                }}>
                                    {scanResult.status === 'ok' && (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <span style={{ fontSize: 28 }}>✅</span>
                                                <div>
                                                    <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: '#3a6b3a', fontWeight: 400 }}>
                                                        Bienvenue, {scanResult.guest.name} !
                                                    </p>
                                                    <p style={{ fontSize: 12, color: '#5a7a5a', marginTop: 2 }}>
                                                        {scanResult.guest.companions === 1 ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{display:'inline',verticalAlign:'middle',marginRight:4}}><circle cx="8" cy="6" r="3" fill="currentColor"/><path d="M2 19c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none"/><circle cx="17" cy="6" r="2.5" fill="currentColor" opacity=".7"/><path d="M13.5 19c0-2.761 1.567-5 3.5-5s3.5 2.239 3.5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity=".7"/></svg>Couple · 2 personnes</> : '1 personne'}
                                                        {scanResult.guest.tableNumber ? ` · Table N°${scanResult.guest.tableNumber}` : ''}
                                                    </p>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                                <button className="btn" style={{ fontSize: 11, padding: '6px 14px', borderColor: '#c0392b', color: '#c0392b' }}
                                                        onClick={() => handleUndoCheckIn(scanResult.guest)}>Annuler</button>
                                                <button className="btn btn-primary" style={{ fontSize: 11, padding: '6px 14px' }}
                                                        onClick={() => setScanResult(null)}>Scanner suivant →</button>
                                            </div>
                                        </div>
                                    )}
                                    {scanResult.status === 'already' && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <span style={{ fontSize: 28 }}>⚠️</span>
                                            <div>
                                                <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: 'var(--gold)', fontWeight: 400 }}>
                                                    {scanResult.guest.name} — déjà enregistré
                                                </p>
                                                <p style={{ fontSize: 12, color: 'var(--mid)', marginTop: 2 }}>
                                                    Enregistré à {scanResult.guest.checkedInAt ? new Date(scanResult.guest.checkedInAt).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'}) : '—'}
                                                </p>
                                                <button className="btn" style={{ fontSize: 11, padding: '4px 12px', marginTop: 8 }}
                                                        onClick={() => setScanResult(null)}>Fermer</button>
                                            </div>
                                        </div>
                                    )}
                                    {scanResult.status === 'notfound' && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <span style={{ fontSize: 28 }}>❌</span>
                                            <div>
                                                <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: '#c0392b', fontWeight: 400 }}>
                                                    Invitation non reconnue
                                                </p>
                                                <p style={{ fontSize: 11, color: 'var(--mid)', marginTop: 2, wordBreak: 'break-all' }}>{scanResult.raw}</p>
                                                <button className="btn" style={{ fontSize: 11, padding: '4px 12px', marginTop: 8 }}
                                                        onClick={() => setScanResult(null)}>Fermer</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Camera view */}
                            <div style={{ position: 'relative', background: '#000', borderRadius: 4, overflow: 'hidden', aspectRatio: '4/3', maxHeight: 380 }}>
                                <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                <canvas ref={canvasRef} style={{ display: 'none' }} />
                                {/* Scan frame overlay */}
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                    <div style={{ width: 200, height: 200, position: 'relative' }}>
                                        {[{top:0,left:0},{top:0,right:0},{bottom:0,left:0},{bottom:0,right:0}].map((pos, i) => (
                                            <div key={i} style={{
                                                position: 'absolute', width: 28, height: 28, ...pos,
                                                borderTop: (i < 2) ? '3px solid white' : 'none',
                                                borderBottom: (i >= 2) ? '3px solid white' : 'none',
                                                borderLeft: (i === 0 || i === 2) ? '3px solid white' : 'none',
                                                borderRight: (i === 1 || i === 3) ? '3px solid white' : 'none',
                                            }} />
                                        ))}
                                        <div style={{
                                            position: 'absolute', top: '50%', left: 0, right: 0, height: 2,
                                            background: 'rgba(201,150,122,0.8)',
                                            animation: 'scanLine 2s ease-in-out infinite',
                                        }} />
                                    </div>
                                </div>
                                {!scanning && !cameraError && (
                                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' }}>
                                        <div style={{ textAlign: 'center', color: 'white' }}>
                                            <div style={{ marginBottom: 12, opacity: 0.85 }}><CameraIcon size={48} style={{color:'white'}}/></div>
                                            <button className="btn btn-primary" onClick={startCamera} style={{ fontSize: 12 }}>Activer la caméra</button>
                                        </div>
                                    </div>
                                )}
                                {cameraError && (
                                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', padding: 20 }}>
                                        <div style={{ textAlign: 'center', color: 'white' }}>
                                            <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
                                            <p style={{ fontSize: 13, marginBottom: 12, color: '#ffcccc' }}>{cameraError}</p>
                                            <button className="btn btn-primary" onClick={startCamera} style={{ fontSize: 12 }}>Réessayer</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <style>{`@keyframes scanLine { 0%,100%{transform:translateY(-80px)} 50%{transform:translateY(80px)} }`}</style>
                            <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--mid)', marginTop: 12, letterSpacing: 1 }}>
                                {scanning ? `Pointez la caméra vers le QR code de l'invitation` : 'Caméra en attente'}
                            </p>
                        </div>
                    )}

                    {/* ── MANUAL MODE ──────────────────────────────────────── */}
                    {mode === 'manual' && (
                        <div>
                            <div style={{ marginBottom: 20, position: 'relative' }}>
                                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#aaa' }}>🔍</span>
                                <input
                                    type="text"
                                    placeholder="Rechercher un invité par nom ou téléphone…"
                                    value={manualSearch}
                                    onChange={e => setManualSearch(e.target.value)}
                                    style={{ paddingLeft: 36, width: '100%' }}
                                    autoFocus
                                />
                            </div>
                            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                                {filteredGuests.length === 0
                                    ? <div style={{ padding: 32, textAlign: 'center', color: 'var(--mid)', fontSize: 13 }}>Aucun invité trouvé</div>
                                    : filteredGuests.slice(0, 30).map(g => (
                                        <div key={g.id} style={{
                                            display: 'flex', alignItems: 'center', gap: 12,
                                            padding: '12px 16px', borderBottom: '1px solid var(--border)',
                                            background: g.checkedIn ? '#f0f8f0' : 'white',
                                        }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 500, fontSize: 14, color: 'var(--dark)' }}>
                                                    {g.name}
                                                    {g.companions === 1 && <span style={{marginLeft:6, color:'var(--gold)', display:'inline-flex', alignItems:'center'}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="6" r="3" fill="currentColor"/><path d="M2 19c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none"/><circle cx="17" cy="6" r="2.5" fill="currentColor" opacity=".7"/><path d="M13.5 19c0-2.761 1.567-5 3.5-5s3.5 2.239 3.5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity=".7"/></svg></span>}
                                                </div>
                                                <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 2 }}>
                                                    {g.tableNumber ? `Table N°${g.tableNumber} · ` : ''}<StatusBadge status={g.status} />
                                                    {g.checkedIn && <span style={{ marginLeft: 8, fontSize: 10, color: '#7a9b7a' }}>
                                                        ✓ {g.checkedInAt ? new Date(g.checkedInAt).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'}) : 'Présent'}
                                                    </span>}
                                                </div>
                                            </div>
                                            {g.checkedIn
                                                ? <button className="btn" style={{ fontSize: 10, padding: '4px 10px', borderColor: '#c0392b', color: '#c0392b', whiteSpace: 'nowrap' }}
                                                          onClick={() => handleUndoCheckIn(g)}>Annuler</button>
                                                : <button className="btn btn-primary" style={{ fontSize: 10, padding: '6px 14px', whiteSpace: 'nowrap' }}
                                                          onClick={() => handleManualCheckIn(g)}>✓ Enregistrer</button>
                                            }
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    )}

                    <div style={{ marginTop: 32, textAlign: 'center' }}>
                        <button className="btn" style={{ fontSize: 11 }} onClick={() => { stopCamera(); navigate(`/${wedding?.id}/dashboard`); }}>
                            ← Retour au Tableau de bord
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

function FallbackRoute() {
    const {sessionLoading} = useApp();
    if (sessionLoading) return <LoadingScreen />;
    return <Navigate to="/" replace />;
}

// ── Protected Route ───────────────────────────────────────────────────────────
function ProtectedRoute({children}) {
    const {isLoggedIn, sessionLoading} = useApp();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!sessionLoading && !isLoggedIn) {
            navigate(`/login?redirect=${location.pathname}`, {replace: true});
        }
    }, [isLoggedIn, sessionLoading]);

    if (sessionLoading) return <LoadingScreen />;
    return isLoggedIn ? children : null;
}

// ── Guest RSVP redirect /:weddingId/guests/:guestId → /:weddingId?rsvp=1 ───────────────
function GuestRsvpRedirect() {
    const {weddingId} = useParams();
    return <Navigate to={`/${weddingId}?rsvp=1`} replace />;
}

// ══════════════════════════════════
//  PWA INSTALL BANNER (Android)
// ══════════════════════════════════
function PwaInstallBanner() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [show, setShow] = useState(false);

    useEffect(() => {
        // Only show on Android browsers that support beforeinstallprompt
        const isAndroid = /android/i.test(navigator.userAgent);
        if (!isAndroid) return;

        // Don't show if already installed (standalone mode)
        if (window.matchMedia('(display-mode: standalone)').matches) return;

        // Don't show if dismissed recently
        const dismissed = localStorage.getItem('pwa_banner_dismissed');
        if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShow(true);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') setShow(false);
        setDeferredPrompt(null);
    };

    const handleDismiss = () => {
        localStorage.setItem('pwa_banner_dismissed', Date.now().toString());
        setShow(false);
    };

    if (!show) return null;

    return (
        <div style={{
            position:'fixed', bottom:0, left:0, right:0, zIndex:9999,
            background:'white', borderTop:'1px solid var(--border)',
            boxShadow:'0 -4px 24px rgba(0,0,0,0.12)',
            padding:'20px 20px 28px',
            animation:'slideUp 0.3s ease',
        }}>
            <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

            {/* Close */}
            <button onClick={handleDismiss} style={{
                position:'absolute', top:12, right:14,
                background:'none', border:'none', fontSize:18,
                color:'var(--mid)', cursor:'pointer', lineHeight:1, padding:4,
            }}>✕</button>

            <div style={{display:'flex', alignItems:'center', gap:14, marginBottom:16}}>
                {/* Icon */}
                <div style={{
                    width:52, height:52, borderRadius:12, flexShrink:0,
                    background:'linear-gradient(135deg, #8b5e4a, #b8985a)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                    <img src="/couple.png" alt=""
                         style={{height:32, width:'auto', filter:'brightness(0) invert(1) opacity(0.9)'}}/>
                </div>
                <div>
                    <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontWeight:400, fontStyle:'italic', color:'var(--rose-dark)'}}>Promessa</div>
                    <div style={{fontSize:11, color:'var(--mid)', marginTop:2}}>Installa l'app per un accesso rapido</div>
                </div>
            </div>

            <button onClick={handleInstall} style={{
                width:'100%', padding:'12px 0', fontSize:13, letterSpacing:1.5,
                textTransform:'uppercase', background:'linear-gradient(135deg, #8b5e4a, #b8985a)',
                color:'white', border:'none', borderRadius:4, cursor:'pointer',
                fontFamily:"'Jost',sans-serif", fontWeight:500,
            }}>
                Installa l'app
            </button>
        </div>
    );
}


const ADMIN_WHATSAPP = '+393511408154';
const ADMIN_WA_URL = `https://wa.me/${ADMIN_WHATSAPP}?text=${encodeURIComponent('Bonjour, je souhaite créer mon invitation de mariage sur Promessa. Pouvez-vous créer mon compte ?')}`;

/**
 * PROMESSA — HomePage redesign
 * Drop-in replacement for the HomePage() function in App.js
 *
 * Requires the same imports already present in App.js:
 *   import React, { useState, useEffect, useRef } from 'react';
 *   import { useNavigate } from 'react-router-dom';
 *
 * And uses the same hooks/context:
 *   useApp(), useIsMobile(), useHeroVideoSrc()
 *   ADMIN_WA_URL constant, Modal component
 *
 * Paste this function body in place of the existing HomePage() in App.js.
 * The <style> block auto-injects into <head> on mount.
 */

/**
 * PROMESSA — HomePage redesign
 * Drop-in replacement for the HomePage() function in App.js
 *
 * Requires the same imports already present in App.js:
 *   import React, { useState, useEffect, useRef } from 'react';
 *   import { useNavigate } from 'react-router-dom';
 *
 * And uses the same hooks/context:
 *   useApp(), useIsMobile(), useHeroVideoSrc()
 *   ADMIN_WA_URL constant, Modal component
 *
 * Paste this function body in place of the existing HomePage() in App.js.
 * The <style> block auto-injects into <head> on mount.
 */

/**
 * PROMESSA — HomePage redesign
 * Drop-in replacement for the HomePage() function in App.js
 *
 * Requires the same imports already present in App.js:
 *   import React, { useState, useEffect, useRef } from 'react';
 *   import { useNavigate } from 'react-router-dom';
 *
 * And uses the same hooks/context:
 *   useApp(), useIsMobile(), useHeroVideoSrc()
 *   ADMIN_WA_URL constant, Modal component
 *
 * Paste this function body in place of the existing HomePage() in App.js.
 * The <style> block auto-injects into <head> on mount.
 */

/**
 * PROMESSA — HomePage redesign
 * Drop-in replacement for the HomePage() function in App.js
 *
 * Requires the same imports already present in App.js:
 *   import React, { useState, useEffect, useRef } from 'react';
 *   import { useNavigate } from 'react-router-dom';
 *
 * And uses the same hooks/context:
 *   useApp(), useIsMobile(), useHeroVideoSrc()
 *   ADMIN_WA_URL constant, Modal component
 *
 * Paste this function body in place of the existing HomePage() in App.js.
 * The <style> block auto-injects into <head> on mount.
 */

/**
 * PROMESSA — HomePage redesign
 * Drop-in replacement for the HomePage() function in App.js
 *
 * Requires the same imports already present in App.js:
 *   import React, { useState, useEffect, useRef } from 'react';
 *   import { useNavigate } from 'react-router-dom';
 *
 * And uses the same hooks/context:
 *   useApp(), useIsMobile(), useHeroVideoSrc()
 *   ADMIN_WA_URL constant, Modal component
 *
 * Paste this function body in place of the existing HomePage() in App.js.
 * The <style> block auto-injects into <head> on mount.
 */

function HomePage() {
    const navigate = useNavigate();
    const { isLoggedIn, wedding } = useApp();
    const [contactOpen, setContactOpen] = useState(false);
    // 'dark' = navbar over dark section (white text), 'light' = over light section (dark text)
    const [navTheme, setNavTheme] = useState('dark');
    const [navSolid, setNavSolid] = useState(false);
    const isMobile = useIsMobile();
    const heroVideoSrc = useHeroVideoSrc();
    const heroRef = useRef(null);
    const featuresRef = useRef(null);
    const stepsRef = useRef(null);

    const handleAlreadyHaveAccount = () => {
        if (isLoggedIn && wedding?.id) navigate(`/${wedding.id}/dashboard`);
        else navigate('/login');
    };

    // Detect which section the navbar sits over — flip text color to match
    useEffect(() => {
        const navH = 70;
        const check = () => {
            // Il nav resta trasparente finché copre hero + stats bar (entrambe scure)
            // Cerca l'ultimo elemento dark consecutivo dall'alto
            const darkSections = Array.from(document.querySelectorAll('[data-hpbg="dark"]'));
            let darkBottom = window.innerHeight;
            let lastBottom = 0;
            for (const el of darkSections) {
                const top = el.offsetTop;
                const bot = top + el.offsetHeight;
                // Solo sezioni contigue dall'inizio (non il CTA o footer in fondo)
                if (top <= lastBottom + 4 || lastBottom === 0) {
                    darkBottom = bot;
                    lastBottom = bot;
                } else {
                    break;
                }
            }
            setNavSolid(window.scrollY + 10 > darkBottom);

            const y = window.scrollY + navH / 2;
            const sections = document.querySelectorAll('[data-hpbg]');
            for (const el of sections) {
                const top = el.offsetTop;
                const bot = top + el.offsetHeight;
                if (y >= top && y < bot) {
                    setNavTheme(el.dataset.hpbg);
                    return;
                }
            }
            setNavTheme('dark');
        };
        window.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check, { passive: true });
        check();
        const t = setTimeout(check, 200); // re-check after paint
        return () => {
            window.removeEventListener('scroll', check);
            window.removeEventListener('resize', check);
            clearTimeout(t);
        };
    }, []);

    // Intersection observer for reveal animations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => entries.forEach(e => {
                if (e.isIntersecting) e.target.classList.add('hp-revealed');
            }),
            { threshold: 0.12 }
        );
        document.querySelectorAll('.hp-reveal').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <>
            {/* ── Injected styles ─────────────────────────────────────────── */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400;1,600&family=Jost:wght@300;400;500&display=swap');

                :root {
                    --hp-cream: #faf7f2;
                    --hp-dark:  #2c2420;
                    --hp-rose:  #8b5e4a;
                    --hp-gold:  #b8985a;
                    --hp-mid:   #a08c80;
                    --hp-border: rgba(139,94,74,0.15);
                }

                /* ── Scroll reveal ── */
                .hp-reveal {
                    opacity: 0;
                    transform: translateY(28px);
                    transition: opacity 0.8s cubic-bezier(.22,.6,.36,1),
                                transform 0.8s cubic-bezier(.22,.6,.36,1);
                }
                .hp-reveal.hp-revealed { opacity: 1; transform: none; }
                .hp-reveal.hp-d1 { transition-delay: 0.1s; }
                .hp-reveal.hp-d2 { transition-delay: 0.22s; }
                .hp-reveal.hp-d3 { transition-delay: 0.34s; }
                .hp-reveal.hp-d4 { transition-delay: 0.46s; }

                /* ── Navbar — trasparente sull'hero, crema dopo ── */
                .hp-nav {
                    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 0 32px; height: 70px;
                    background: transparent;
                    border-bottom: 1px solid transparent;
                    box-shadow: none;
                    transition: background 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease;
                }
                .hp-nav--solid {
                    background: #faf7f2;
                    border-bottom: 1px solid rgba(139,94,74,0.12);
                    box-shadow: 0 2px 16px rgba(44,36,32,0.06);
                }
                /* Logo crema sull'hero (nav trasparente) — DOPO .hp-nav-logo per vincere la cascata */
                .hp-nav-logo {
                    display: flex; align-items: center; gap: 8px;
                    font-family: 'Cormorant Garamond', serif;
                    font-style: italic; font-weight: 300; font-size: 24px;
                    letter-spacing: 1px; text-decoration: none;
                    color: var(--hp-rose);
                    transition: color 0.35s ease;
                }
                .hp-nav-logo img { height: 28px; width: auto; filter: none; }
                .hp-nav-actions { display: flex; gap: 10px; align-items: center; }
                /* Override trasparente — scritto DOPO i default per vincere */
                .hp-nav:not(.hp-nav--solid) .hp-nav-logo,
                .hp-nav:not(.hp-nav--solid) .hp-nav-logo span {
                    color: #faf7f2;
                }
                .hp-nav:not(.hp-nav--solid) .hp-btn-ghost {
                    border-color: rgba(255,255,255,0.4);
                    color: rgba(255,255,255,0.85);
                }
                .hp-nav:not(.hp-nav--solid) .hp-btn-ghost:hover {
                    background: rgba(255,255,255,0.1);
                }
                .hp-nav:not(.hp-nav--solid) .hp-btn-primary {
                    background: rgba(184,152,90,0.9);
                    border-color: rgba(184,152,90,0.9);
                    color: var(--hp-dark);
                }
                .hp-btn-ghost {
                    background: none; border: 1px solid rgba(139,94,74,0.35);
                    color: var(--hp-rose); padding: 8px 22px; font-size: 11px;
                    letter-spacing: 1.8px; text-transform: uppercase;
                    font-family: 'Jost', sans-serif; font-weight: 400;
                    cursor: pointer; transition: all 0.2s; border-radius: 2px;
                }
                .hp-btn-ghost:hover { background: rgba(139,94,74,0.07); }
                .hp-btn-primary {
                    background: var(--hp-rose); border: 1px solid var(--hp-rose);
                    color: white; padding: 8px 24px; font-size: 11px;
                    letter-spacing: 1.8px; text-transform: uppercase;
                    font-family: 'Jost', sans-serif; font-weight: 500;
                    cursor: pointer; border-radius: 2px;
                    transition: background 0.2s, transform 0.15s;
                }
                .hp-btn-primary:hover { background: #7a4f3a; transform: translateY(-1px); }

                /* ── Hero ── */
                .hp-hero {
                    position: relative; min-height: 100svh;
                    display: flex; flex-direction: column;
                    align-items: center; justify-content: center;
                    text-align: center; overflow: hidden;
                    padding: 140px 24px 100px;
                    margin-top: 0;
                }
                .hp-hero-video {
                    position: absolute; inset: 0;
                    width: 100%; height: 100%;
                    object-fit: cover; z-index: 0;
                }
                .hp-hero-overlay {
                    position: absolute; inset: 0; z-index: 1;
                    background: linear-gradient(
                        175deg,
                        rgba(30,18,14,0.72) 0%,
                        rgba(44,26,18,0.58) 45%,
                        rgba(30,18,14,0.80) 100%
                    );
                }
                /* Cinematic vignette */
                .hp-hero-vignette {
                    position: absolute; inset: 0; z-index: 2;
                    background: radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.55) 100%);
                }
                /* Decorative rings */
                .hp-ring {
                    position: absolute; border-radius: 50%; z-index: 2;
                    top: 50%; left: 50%; transform: translate(-50%,-50%);
                    animation: hp-ring-pulse 6s ease-in-out infinite;
                }
                @keyframes hp-ring-pulse {
                    0%, 100% { opacity: 0.6; }
                    50%       { opacity: 1; }
                }
                .hp-hero-content {
                    position: relative; z-index: 3; max-width: 700px;
                }
                .hp-eyebrow {
                    font-family: 'Jost', sans-serif; font-size: 10px;
                    font-weight: 400; letter-spacing: 5px; text-transform: uppercase;
                    color: rgba(255,255,255,0.55); margin-bottom: 28px;
                    animation: hp-fade-up 1.1s cubic-bezier(.22,.6,.36,1) 0.2s both;
                }
                .hp-headline {
                    font-family: 'Cormorant Garamond', serif;
                    font-size: clamp(52px, 8.5vw, 96px);
                    font-weight: 300; font-style: italic; color: white;
                    line-height: 1.05; margin-bottom: 0;
                    animation: hp-fade-up 1.1s cubic-bezier(.22,.6,.36,1) 0.38s both;
                }
                .hp-headline em { font-style: normal; color: rgba(184,152,90,0.9); }
                .hp-divider {
                    width: 1px; height: 60px;
                    background: linear-gradient(to bottom, transparent, rgba(255,255,255,0.35), transparent);
                    margin: 36px auto;
                    animation: hp-fade-up 1.1s cubic-bezier(.22,.6,.36,1) 0.52s both;
                }
                .hp-subline {
                    font-family: 'Jost', sans-serif; font-size: 15px; font-weight: 300;
                    color: rgba(255,255,255,0.7); line-height: 1.9; max-width: 420px;
                    margin: 0 auto 52px;
                    animation: hp-fade-up 1.1s cubic-bezier(.22,.6,.36,1) 0.62s both;
                }
                .hp-cta-group {
                    display: flex; gap: 14px; justify-content: center; flex-wrap: wrap;
                    animation: hp-fade-up 1.1s cubic-bezier(.22,.6,.36,1) 0.76s both;
                }
                .hp-cta-main {
                    background: var(--hp-gold); border: none; color: var(--hp-dark);
                    padding: 16px 44px; font-family: 'Jost', sans-serif;
                    font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase;
                    font-weight: 500; cursor: pointer; border-radius: 2px;
                    transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
                    box-shadow: 0 4px 32px rgba(184,152,90,0.3);
                }
                .hp-cta-main:hover {
                    background: #cba96a; transform: translateY(-2px);
                    box-shadow: 0 8px 40px rgba(184,152,90,0.45);
                }
                .hp-cta-secondary {
                    background: none; border: 1px solid rgba(255,255,255,0.35);
                    color: white; padding: 16px 36px; font-family: 'Jost', sans-serif;
                    font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase;
                    font-weight: 400; cursor: pointer; border-radius: 2px;
                    transition: background 0.25s, border-color 0.25s, transform 0.2s;
                }
                .hp-cta-secondary:hover {
                    background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.6);
                    transform: translateY(-2px);
                }
                .hp-scroll-cue {
                    position: absolute; bottom: 36px; left: 50%;
                    transform: translateX(-50%); z-index: 3;
                    display: flex; flex-direction: column; align-items: center; gap: 8px;
                    animation: hp-fade-up 1s cubic-bezier(.22,.6,.36,1) 1.2s both;
                }
                .hp-scroll-line {
                    width: 1px; height: 48px;
                    background: linear-gradient(to bottom, rgba(255,255,255,0.5), transparent);
                    animation: hp-scroll-line 2s ease-in-out infinite;
                }
                @keyframes hp-scroll-line {
                    0%   { transform: scaleY(0); transform-origin: top; }
                    50%  { transform: scaleY(1); transform-origin: top; }
                    50.01% { transform-origin: bottom; }
                    100% { transform: scaleY(0); transform-origin: bottom; }
                }
                .hp-scroll-label {
                    font-family: 'Jost', sans-serif; font-size: 9px;
                    letter-spacing: 3px; text-transform: uppercase;
                    color: rgba(255,255,255,0.35);
                }

                @keyframes hp-fade-up {
                    from { opacity: 0; transform: translateY(24px); }
                    to   { opacity: 1; transform: none; }
                }

                /* ── Stats bar ── */
                .hp-stats {
                    background: var(--hp-dark);
                    display: flex; align-items: center; justify-content: center;
                    flex-wrap: wrap; gap: 0;
                    border-bottom: 1px solid rgba(255,255,255,0.06);
                }
                .hp-stat {
                    flex: 1; min-width: 140px;
                    display: flex; flex-direction: column; align-items: center;
                    justify-content: center; padding: 32px 20px;
                    border-right: 1px solid rgba(255,255,255,0.06);
                    position: relative;
                }
                .hp-stat:last-child { border-right: none; }
                .hp-stat-num {
                    font-family: 'Cormorant Garamond', serif;
                    font-size: 40px; font-weight: 300; font-style: italic;
                    color: var(--hp-gold); line-height: 1;
                }
                .hp-stat-label {
                    font-family: 'Jost', sans-serif; font-size: 9px;
                    letter-spacing: 2.5px; text-transform: uppercase;
                    color: rgba(255,255,255,0.35); margin-top: 8px;
                }

                /* ── Features ── */
                .hp-features {
                    background: var(--hp-cream); padding: 120px 24px;
                    position: relative; overflow: hidden;
                }
                .hp-features::before {
                    content: ''; position: absolute;
                    top: -1px; left: 0; right: 0; height: 1px;
                    background: linear-gradient(to right, transparent, var(--hp-gold), transparent);
                }
                .hp-section-eyebrow {
                    font-family: 'Jost', sans-serif; font-size: 9px;
                    letter-spacing: 4px; text-transform: uppercase; color: var(--hp-gold);
                    margin-bottom: 20px; display: block; text-align: center;
                }
                .hp-section-title {
                    font-family: 'Cormorant Garamond', serif;
                    font-size: clamp(34px, 4vw, 52px); font-weight: 300;
                    font-style: italic; color: var(--hp-rose); line-height: 1.15;
                    text-align: center; margin-bottom: 72px;
                }
                .hp-features-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 2px; max-width: 980px; margin: 0 auto;
                    background: var(--hp-border);
                    border: 1px solid var(--hp-border);
                }
                @media (max-width: 860px) {
                    .hp-features-grid { grid-template-columns: repeat(2, 1fr); }
                }
                @media (max-width: 520px) {
                    .hp-features-grid { grid-template-columns: 1fr; }
                }
                .hp-feature-card {
                    background: var(--hp-cream); padding: 52px 40px;
                    display: flex; flex-direction: column; align-items: center;
                    text-align: center; position: relative;
                    transition: background 0.3s;
                }
                .hp-feature-card:hover { background: white; }
                .hp-feature-icon {
                    width: 64px; height: 64px; margin-bottom: 28px;
                    position: relative; flex-shrink: 0;
                }
                .hp-feature-icon::after {
                    content: ''; position: absolute;
                    bottom: -8px; left: 50%; transform: translateX(-50%);
                    width: 24px; height: 1px; background: var(--hp-gold);
                }
                .hp-feature-title {
                    font-family: 'Cormorant Garamond', serif; font-size: 22px;
                    font-weight: 400; color: var(--hp-dark); margin-bottom: 14px;
                    margin-top: 12px;
                }
                .hp-feature-desc {
                    font-family: 'Jost', sans-serif; font-size: 13px;
                    font-weight: 300; color: var(--hp-mid); line-height: 1.9;
                }

                /* ── Steps section ── */
                .hp-steps {
                    background: white; padding: 120px 24px;
                    border-top: 1px solid var(--hp-border);
                }
                .hp-steps-inner {
                    max-width: 1060px; margin: 0 auto;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 80px; align-items: center;
                }
                @media (max-width: 680px) {
                    .hp-steps-inner { grid-template-columns: 1fr; gap: 52px; }
                    .hp-steps-preview { display: none; }
                }
                .hp-step-item {
                    display: flex; align-items: flex-start; gap: 20px; margin-bottom: 28px;
                }
                .hp-step-num {
                    width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
                    border: 1px solid var(--hp-gold); color: var(--hp-gold);
                    display: flex; align-items: center; justify-content: center;
                    font-family: 'Cormorant Garamond', serif; font-size: 16px;
                    font-style: italic; font-weight: 400; margin-top: 1px;
                }
                .hp-step-body-title {
                    font-family: 'Jost', sans-serif; font-size: 13px;
                    font-weight: 500; color: var(--hp-dark); margin-bottom: 4px;
                    letter-spacing: 0.5px;
                }
                .hp-step-body-desc {
                    font-family: 'Jost', sans-serif; font-size: 13px;
                    font-weight: 300; color: var(--hp-mid); line-height: 1.8;
                }
                /* Mock invitation card */
                .hp-mock-card {
                    border: 1px solid var(--hp-border);
                    background: var(--hp-cream); overflow: hidden;
                    max-width: 340px; margin: 0 auto;
                    box-shadow: 0 24px 80px rgba(44,36,32,0.12),
                                0 2px 8px rgba(44,36,32,0.06);
                }
                .hp-mock-photo {
                    width: 100%; height: 260px; overflow: hidden;
                    position: relative;
                }
                .hp-mock-photo img {
                    width: 100%; height: 100%;
                    object-fit: cover; object-position: center 15%;
                    transition: transform 5s ease;
                }
                .hp-mock-card:hover .hp-mock-photo img { transform: scale(1.04); }
                .hp-mock-photo-overlay {
                    position: absolute; inset: 0;
                    background: linear-gradient(to bottom, transparent 55%, rgba(44,36,32,0.35));
                }
                .hp-mock-body {
                    padding: 28px 32px; text-align: center;
                }
                .hp-mock-ornament {
                    font-size: 12px; letter-spacing: 6px; color: var(--hp-gold); margin-bottom: 10px;
                }
                .hp-mock-subtitle {
                    font-family: 'Jost', sans-serif; font-size: 8px;
                    letter-spacing: 2px; text-transform: uppercase; color: var(--hp-gold);
                    margin-bottom: 10px;
                }
                .hp-mock-names {
                    font-family: 'Cormorant Garamond', serif; font-size: 34px;
                    font-weight: 300; font-style: italic; color: var(--hp-rose);
                    line-height: 1.1; margin-bottom: 4px;
                }
                .hp-mock-et {
                    font-size: 16px; color: var(--hp-gold); display: block; margin: 2px 0;
                }
                .hp-mock-date {
                    margin: 18px 0 12px; padding: 14px 20px;
                    border: 1px solid var(--hp-border); background: white;
                    font-family: 'Cormorant Garamond', serif; font-size: 18px;
                    font-weight: 300; color: var(--hp-dark);
                }
                .hp-mock-venue {
                    font-family: 'Jost', sans-serif; font-size: 10px;
                    color: var(--hp-mid); letter-spacing: 0.5px; margin-bottom: 16px;
                    line-height: 1.6;
                }
                .hp-mock-rsvp {
                    background: var(--hp-rose); color: white; padding: 12px;
                    font-family: 'Jost', sans-serif; font-size: 9px;
                    letter-spacing: 2px; text-transform: uppercase; font-weight: 500;
                    cursor: pointer; transition: background 0.2s;
                }
                .hp-mock-rsvp:hover { background: #7a4f3a; }

                /* ── CTA bottom ── */
                .hp-cta-section {
                    position: relative; overflow: hidden;
                    background: var(--hp-dark); padding: 120px 24px; text-align: center;
                }
                .hp-cta-section::before {
                    content: '';
                    position: absolute; inset: 0;
                    background: radial-gradient(ellipse at 50% 100%, rgba(184,152,90,0.12) 0%, transparent 70%);
                }
                .hp-cta-title {
                    font-family: 'Cormorant Garamond', serif;
                    font-size: clamp(38px, 5vw, 64px); font-weight: 300;
                    font-style: italic; color: white; margin-bottom: 20px; line-height: 1.1;
                    position: relative;
                }
                .hp-cta-desc {
                    font-family: 'Jost', sans-serif; font-size: 14px; font-weight: 300;
                    color: rgba(255,255,255,0.5); max-width: 400px; margin: 0 auto 52px;
                    line-height: 1.9; position: relative;
                }
                .hp-cta-ring {
                    position: absolute; border-radius: 50%;
                    border: 1px solid rgba(184,152,90,0.1);
                    top: 50%; left: 50%; transform: translate(-50%, -50%);
                    pointer-events: none;
                }

                /* ── Footer ── */
                .hp-footer {
                    background: var(--hp-dark);
                    border-top: 1px solid rgba(255,255,255,0.05);
                    padding: 28px 32px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 12px;
                }
                .hp-footer-brand {
                    font-family: 'Cormorant Garamond', serif; font-style: italic;
                    font-weight: 300; font-size: 18px; color: rgba(255,255,255,0.4);
                    letter-spacing: 1px;
                }
                .hp-footer-copy {
                    font-family: 'Jost', sans-serif; font-size: 10px;
                    letter-spacing: 1.5px; color: rgba(255,255,255,0.2);
                }

                /* ── Global reset for this page ── */
                body { margin: 0 !important; padding: 0 !important; }
                html, body { overflow-x: hidden; }
                #root { padding-top: 0 !important; margin-top: 0 !important; }
                /* Hide the App-level <nav> if it renders above the hero */
                body > #root > div > nav:not(.hp-nav) { display: none !important; }

                /* ── Mobile tweaks ── */
                @media (max-width: 600px) {
                    .hp-nav { padding: 0 20px; height: 60px; }
                    .hp-stat { min-width: 50%; border-bottom: 1px solid rgba(255,255,255,0.06); }
                    .hp-feature-card { padding: 40px 28px; }
                }
            `}</style>

            {/* ══ NAVBAR ════════════════════════════════════════════════════ */}
            <nav className={`hp-nav${navSolid ? ' hp-nav--solid' : ''}`}>
                <div className="hp-nav-logo">
                    <img
                        src="/couple.png"
                        alt=""
                        style={{ height: 28, width: 'auto', display: 'block', filter: navSolid ? 'none' : 'brightness(0) invert(1) opacity(0.9)' }}
                    />
                    <span>Promessa</span>
                </div>
                {!isMobile && (
                    <div className="hp-nav-actions">
                        <button className="hp-btn-ghost" onClick={handleAlreadyHaveAccount}>
                            Se connecter
                        </button>
                        <button className="hp-btn-primary" onClick={() => setContactOpen(true)}>
                            Commencer
                        </button>
                    </div>
                )}
            </nav>

            {/* ══ HERO ══════════════════════════════════════════════════════ */}
            <section className="hp-hero" ref={heroRef} style={{ marginTop: 0 }} data-hpbg="dark">
                <video className="hp-hero-video" autoPlay muted loop playsInline>
                    <source src={heroVideoSrc} type="video/mp4" />
                </video>
                <div className="hp-hero-overlay" />
                <div className="hp-hero-vignette" />

                {/* Decorative rings */}
                <div className="hp-ring" style={{ width: 440, height: 440, border: '1px solid rgba(184,152,90,0.12)' }} />
                <div className="hp-ring" style={{ width: 680, height: 680, border: '1px solid rgba(255,255,255,0.05)', animationDelay:'1.5s' }} />
                <div className="hp-ring" style={{ width: 920, height: 920, border: '1px solid rgba(255,255,255,0.03)', animationDelay:'3s' }} />

                <div className="hp-hero-content">
                    <p className="hp-eyebrow">Votre mariage, simplement magnifique</p>

                    <h1 className="hp-headline">
                        L'amour mérite<br/>
                        une invitation <em>parfaite</em>
                    </h1>

                    <div className="hp-divider" />

                    <p className="hp-subline">
                        Créez votre invitation digitale, gérez vos invités
                        et recevez les RSVP — le tout depuis une seule page élégante.
                    </p>

                    <div className="hp-cta-group">
                        <button className="hp-cta-main" onClick={() => setContactOpen(true)}>
                            Créer mon invitation
                        </button>
                        <button className="hp-cta-secondary" onClick={handleAlreadyHaveAccount}>
                            J'ai déjà un compte
                        </button>
                    </div>
                </div>

                <div className="hp-scroll-cue">
                    <div className="hp-scroll-line" />
                    <span className="hp-scroll-label">Découvrir</span>
                </div>
            </section>

            {/* ══ STATS BAR ═════════════════════════════════════════════════ */}
            <div className="hp-stats" data-hpbg="dark">
                {[
                    { num: '500+', label: 'Mariages célébrés' },
                    { num: '98%',  label: 'Couples satisfaits' },
                    { num: '5 min', label: 'Pour commencer' },
                    { num: '∞',    label: 'Invités possibles' },
                ].map((s, i) => (
                    <div key={i} className="hp-stat hp-reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                        <span className="hp-stat-num">{s.num}</span>
                        <span className="hp-stat-label">{s.label}</span>
                    </div>
                ))}
            </div>

            {/* ══ FEATURES ══════════════════════════════════════════════════ */}
            <section className="hp-features" ref={featuresRef} data-hpbg="light">
                <div className="hp-reveal">
                    <span className="hp-section-eyebrow">Ce que nous offrons</span>
                    <h2 className="hp-section-title">
                        Tout ce dont vous avez besoin,<br/>
                        rien de superflu
                    </h2>
                </div>

                <div className="hp-features-grid">
                    {[
                        {
                            title: 'Invitation digitale',
                            desc: 'Une invitation élégante avec votre photo, vos couleurs, et votre message — partageable en un clic.',
                            icon: (
                                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                                    <rect x="8" y="16" width="48" height="34" rx="2" stroke="#8b5e4a" strokeWidth="1.5"/>
                                    <path d="M8,19 L32,36 L56,19" stroke="#8b5e4a" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
                                    <path d="M8,50 L22,36" stroke="#8b5e4a" strokeWidth="1" opacity="0.4" fill="none"/>
                                    <path d="M56,50 L42,36" stroke="#8b5e4a" strokeWidth="1" opacity="0.4" fill="none"/>
                                    <path d="M28,29 C28,26.5 25,24.5 25,27.5 C25,29.5 28,31.5 28,31.5 C28,31.5 31,29.5 31,27.5 C31,24.5 28,26.5 28,29Z" fill="#b8985a" opacity="0.9"/>
                                </svg>
                            ),
                        },
                        {
                            title: 'Gestion des invités',
                            desc: 'Tableau de bord intuitif pour gérer votre liste, les tables, les RSVP et les présences en temps réel.',
                            icon: (
                                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                                    <circle cx="24" cy="22" r="7" stroke="#8b5e4a" strokeWidth="1.5" fill="none"/>
                                    <path d="M10,50 C10,39 38,39 38,50" stroke="#8b5e4a" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                                    <circle cx="42" cy="19" r="5.5" stroke="#b8985a" strokeWidth="1.5" fill="none"/>
                                    <path d="M32,47 C34,40 54,40 54,47" stroke="#b8985a" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                                    <circle cx="50" cy="50" r="6" stroke="#8b5e4a" strokeWidth="1" fill="none"/>
                                    <text x="50" y="53.5" textAnchor="middle" fontSize="7" fill="#8b5e4a" fontFamily="serif">T</text>
                                </svg>
                            ),
                        },
                        {
                            title: 'RSVP automatique',
                            desc: 'Chaque invité reçoit son lien WhatsApp personnel et confirme en un tap. Les résultats arrivent en direct.',
                            icon: (
                                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                                    <rect x="20" y="8" width="24" height="48" rx="3" stroke="#8b5e4a" strokeWidth="1.5" fill="none"/>
                                    <line x1="20" y1="16" x2="44" y2="16" stroke="#8b5e4a" strokeWidth="1"/>
                                    <line x1="20" y1="48" x2="44" y2="48" stroke="#8b5e4a" strokeWidth="1"/>
                                    <circle cx="32" cy="54" r="2" fill="#8b5e4a" opacity="0.4"/>
                                    <path d="M24,32 L29,37 L40,26" stroke="#b8985a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                    <path d="M46,22 C49,25 49,31 46,34" stroke="#8b5e4a" strokeWidth="1.3" strokeLinecap="round" fill="none" opacity="0.5"/>
                                    <path d="M50,18 C54,22 54,34 50,38" stroke="#8b5e4a" strokeWidth="1.3" strokeLinecap="round" fill="none" opacity="0.28"/>
                                </svg>
                            ),
                        },
                        {
                            title: 'Contrôle d\'accès',
                            desc: 'Scanner QR intégré pour valider les entrées le jour J. Zéro papier, zéro stress, zéro raté.',
                            icon: (
                                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                                    <rect x="10" y="18" width="28" height="22" rx="2.5" stroke="#8b5e4a" strokeWidth="1.5" fill="none"/>
                                    <circle cx="24" cy="29" r="5" stroke="#8b5e4a" strokeWidth="1.5" fill="none"/>
                                    <circle cx="24" cy="29" r="2" fill="#8b5e4a" opacity="0.5"/>
                                    <path d="M10,22 C8,20 8,18 10,18" stroke="#8b5e4a" strokeWidth="1.3" fill="none" strokeLinecap="round"/>
                                    <path d="M38,18 C40,18 40,20 38,22" stroke="#8b5e4a" strokeWidth="1.3" fill="none" strokeLinecap="round"/>
                                    {/* QR pattern on right */}
                                    <rect x="42" y="18" width="5" height="5" stroke="#b8985a" strokeWidth="1" fill="none"/>
                                    <rect x="42" y="27" width="5" height="5" stroke="#b8985a" strokeWidth="1" fill="none"/>
                                    <rect x="51" y="18" width="5" height="5" stroke="#b8985a" strokeWidth="1" fill="none"/>
                                    <rect x="49" y="27" width="3" height="3" fill="#b8985a" opacity="0.5"/>
                                    <rect x="54" y="27" width="2" height="5" stroke="#b8985a" strokeWidth="1" fill="none"/>
                                    <line x1="14" y1="44" x2="54" y2="44" stroke="#8b5e4a" strokeWidth="1" strokeDasharray="4,4" opacity="0.3"/>
                                    <path d="M24,48 L30,54 L44,44" stroke="#b8985a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                </svg>
                            ),
                        },
                    ].map((f, i) => (
                        <div key={i} className={`hp-feature-card hp-reveal hp-d${(i % 4) + 1}`}>
                            <div className="hp-feature-icon">{f.icon}</div>
                            <h3 className="hp-feature-title">{f.title}</h3>
                            <p className="hp-feature-desc">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ══ HOW IT WORKS + MOCK ═══════════════════════════════════════ */}
            <section className="hp-steps" ref={stepsRef} data-hpbg="light">
                <div className="hp-steps-inner">
                    {/* Left */}
                    <div>
                        <span className="hp-section-eyebrow" style={{ textAlign: 'left' }}>Comment ça marche</span>
                        <h2 className="hp-section-title hp-reveal" style={{ textAlign: 'left', fontSize: 'clamp(30px,3.5vw,44px)' }}>
                            Prêt en quelques minutes,<br/>
                            beau pour toujours
                        </h2>
                        <p style={{ fontFamily: "'Jost',sans-serif", fontSize: 13, color: 'var(--hp-mid)', lineHeight: 1.9, marginBottom: 36, fontWeight: 300 }}>
                            De la création du compte à l'envoi des invitations, tout est pensé pour être simple et impeccable.
                        </p>

                        {[
                            { n: '1', title: 'Contactez-nous', desc: 'Envoyez un message sur WhatsApp. Votre compte est créé en quelques minutes.' },
                            { n: '2', title: 'Personnalisez', desc: 'Ajoutez votre photo, vos noms, la date, le lieu et votre message personnel.' },
                            { n: '3', title: 'Partagez', desc: 'Envoyez les invitations par WhatsApp. Les RSVP arrivent automatiquement.' },
                        ].map((s, i) => (
                            <div key={s.n} className={`hp-step-item hp-reveal hp-d${i + 1}`}>
                                <div className="hp-step-num">{s.n}</div>
                                <div>
                                    <p className="hp-step-body-title">{s.title}</p>
                                    <p className="hp-step-body-desc">{s.desc}</p>
                                </div>
                            </div>
                        ))}

                        <div className="hp-reveal hp-d4" style={{ marginTop: 40 }}>
                            <button className="hp-cta-main" onClick={() => setContactOpen(true)}>
                                Commencer maintenant
                            </button>
                        </div>
                    </div>

                    {/* Right — invitation mock */}
                    <div className="hp-steps-preview hp-reveal hp-d2">
                        <div className="hp-mock-card">
                            <div className="hp-mock-photo">
                                <img src="/nigerian-wedding_.webp" alt="Couple" loading="lazy" />
                                <div className="hp-mock-photo-overlay" />
                            </div>
                            <div className="hp-mock-body">
                                <div className="hp-mock-ornament">✦ ✦ ✦</div>
                                <p className="hp-mock-subtitle">Vous êtes cordialement invité(e)</p>
                                <div className="hp-mock-names">
                                    Yasmine
                                    <span className="hp-mock-et">&</span>
                                    Mike
                                </div>
                                <div className="hp-mock-date">21 septembre 2025</div>
                                <p className="hp-mock-venue">
                                    Salle des Fêtes du Palais du Peuple<br/>
                                    Kinshasa, RDC · 15h00
                                </p>
                                <div className="hp-mock-rsvp">Confirmer ma présence →</div>
                            </div>
                        </div>
                        <p style={{ fontFamily: "'Jost',sans-serif", fontSize: 9, color: 'var(--hp-gold)', textAlign: 'center', marginTop: 12, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                            Aperçu de l'invitation
                        </p>
                    </div>
                </div>
            </section>

            {/* ══ CTA BOTTOM ════════════════════════════════════════════════ */}
            <section className="hp-cta-section" data-hpbg="dark">
                {/* Background rings */}
                <div className="hp-cta-ring" style={{ width: 500, height: 500, opacity: 0.6 }} />
                <div className="hp-cta-ring" style={{ width: 800, height: 800, opacity: 0.35 }} />

                <div className="hp-reveal" style={{ position: 'relative' }}>
                    <span style={{ display: 'block', fontFamily: "'Jost',sans-serif", fontSize: 9, letterSpacing: 4, textTransform: 'uppercase', color: 'rgba(184,152,90,0.6)', marginBottom: 24 }}>
                        ✦ Promessa ✦
                    </span>
                    <h2 className="hp-cta-title">
                        Votre grand jour<br/>mérite le grand art
                    </h2>
                    <p className="hp-cta-desc">
                        Rejoignez des centaines de couples qui ont fait confiance à Promessa pour sublimer leur invitation.
                    </p>
                    <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button className="hp-cta-main" onClick={() => setContactOpen(true)}>
                            Créer mon espace
                        </button>
                        <button
                            onClick={handleAlreadyHaveAccount}
                            style={{
                                background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                                color: 'rgba(255,255,255,0.65)', padding: '16px 36px',
                                fontFamily: "'Jost',sans-serif", fontSize: 11,
                                letterSpacing: 2.5, textTransform: 'uppercase',
                                fontWeight: 400, cursor: 'pointer', borderRadius: 2,
                                transition: 'all 0.25s',
                            }}
                        >
                            Me connecter
                        </button>
                    </div>
                </div>
            </section>

            {/* ══ FOOTER ════════════════════════════════════════════════════ */}
            <footer className="hp-footer" data-hpbg="dark">
                <span className="hp-footer-brand">Promessa</span>
                <span className="hp-footer-copy">© {new Date().getFullYear()} · Invitation digitale de mariage</span>
            </footer>

            {/* ══ CONTACT MODAL ═════════════════════════════════════════════ */}
            <Modal id="contactModal" open={contactOpen} onClose={() => setContactOpen(false)}>
                <div className="modal-box">
                    <button className="modal-close" onClick={() => setContactOpen(false)}>✕</button>
                    <div className="modal-header">
                        <div className="ornament" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>✦ ✦ ✦</div>
                        <h2 className="serif" style={{ fontSize: 26, fontWeight: 300, fontStyle: 'italic', color: 'white' }}>
                            Créer votre espace
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, letterSpacing: 1, marginTop: 8 }}>
                            Promessa · Invitation digitale
                        </p>
                    </div>
                    <div className="modal-body" style={{ textAlign: 'center' }}>
                        <div className="ornament" style={{ marginBottom: 20 }}>✦</div>
                        <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 19, fontStyle: 'italic', color: 'var(--rose-dark)', lineHeight: 1.6, marginBottom: 20 }}>
                            Pour créer votre espace mariage,<br />contactez-nous sur WhatsApp.
                        </p>
                        <p style={{ fontSize: 13, color: 'var(--mid)', lineHeight: 1.8, marginBottom: 28 }}>
                            Nous créons votre compte en quelques minutes et vous envoyons vos identifiants pour commencer à configurer votre invitation.
                        </p>
                        <div style={{ textAlign: 'left', marginBottom: 32 }}>
                            {[
                                { n: '1', label: 'Contactez-nous via WhatsApp' },
                                { n: '2', label: 'Nous créons votre compte' },
                                { n: '3', label: 'Vous recevez vos identifiants' },
                                { n: '4', label: 'Configurez votre invitation' },
                            ].map(s => (
                                <div key={s.n} style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                                    <div style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--gold)', color: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontFamily: "'Cormorant Garamond',serif", fontStyle: 'italic', flexShrink: 0 }}>
                                        {s.n}
                                    </div>
                                    <p style={{ fontSize: 13, color: 'var(--dark)' }}>{s.label}</p>
                                </div>
                            ))}
                        </div>
                        <a href={ADMIN_WA_URL} target="_blank" rel="noreferrer"
                           style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#25D366', color: 'white', padding: '14px 28px', textDecoration: 'none', fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', width: '100%', boxSizing: 'border-box', fontFamily: "'Jost',sans-serif", fontWeight: 500 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                            Nous contacter sur WhatsApp
                        </a>
                        <p style={{ marginTop: 16, fontSize: 11, color: 'var(--mid)', letterSpacing: .5 }}>
                            Déjà un compte ?{' '}
                            <button style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}
                                    onClick={() => { setContactOpen(false); navigate('/login'); }}>
                                Se connecter
                            </button>
                        </p>
                    </div>
                </div>
            </Modal>
        </>
    );
}

// ══════════════════════════════════
//  PAGE: LOGIN  /login
// ══════════════════════════════════
function LoginPage() {
    const {login} = useApp();
    const [user, setUser]       = useState('');
    const [pass, setPass]       = useState('');
    const [err,  setErr]        = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const doLogin = async () => {
        if (!user || !pass) { setErr('Veuillez entrer votre identifiant et mot de passe.'); return; }
        setLoading(true);
        try { await login(user, pass); }
        catch(e) { setErr(e.message); }
        finally { setLoading(false); }
    };

    return (
        <>
            <Nav />
            <div style={{minHeight:'calc(100vh - 60px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20}}>
                <div style={{maxWidth:440, width:'100%'}}>
                    <div style={{textAlign:'center', marginBottom:40}}>
                        <div className="ornament">✦</div>
                        <h2 className="serif" style={{fontSize:36, fontWeight:300, fontStyle:'italic', color:'var(--rose-dark)'}}>Espace Époux</h2>
                        <p style={{color:'var(--mid)', fontSize:13, letterSpacing:1, marginTop:8}}>Gérez votre mariage et vos invités</p>
                    </div>
                    <div className="card">
                        <div className="form-group">
                            <label>Identifiant</label>
                            <input type="text" placeholder="Votre identifiant" value={user}
                                   onChange={e => setUser(e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label>Mot de passe</label>
                            <input type="text" placeholder="Votre mot de passe" value={pass}
                                   onChange={e => setPass(e.target.value)}
                                   onKeyDown={e => e.key === 'Enter' && doLogin()}
                                   autoComplete="current-password"
                                   data-form-type="password"
                                   aria-label="Mot de passe" />
                        </div>
                        {err && <div style={{color:'#c0392b', fontSize:12, marginBottom:12}}>{err}</div>}
                        <button className="btn btn-primary" style={{width:'100%'}} onClick={doLogin} disabled={loading}>
                            {loading ? 'Connexion…' : 'Se connecter'}
                        </button>
                        {/*<hr className="divider"/>*/}
                        {/*<p style={{textAlign:'center', fontSize:12, color:'var(--mid)'}}>*/}
                        {/*    Première visite ?{' '}*/}
                        {/*    <button style={{background:'none', border:'none', color:'var(--gold)', cursor:'pointer', fontSize:12, textDecoration:'underline'}}*/}
                        {/*            onClick={() => navigate('/setup')}>Configurer votre mariage</button>*/}
                        {/*</p>*/}
                    </div>
                    <p style={{textAlign:'center', marginTop:20, fontSize:12, color:'var(--mid)'}}>
                        <button style={{background:'none', border:'none', color:'var(--mid)', cursor:'pointer', fontSize:12}}
                                onClick={() => navigate('/')}>← Retour à l'accueil</button>
                    </p>
                </div>
            </div>
        </>
    );
}

// ══════════════════════════════════
//  SETUP FORM (shared by SetupPage & SettingsPage)
// ══════════════════════════════════
function PhotoPositionPicker({photoData, position, onChange, onChangeFinal}) {
    const containerRef = useRef(null);
    const isDragging = useRef(false);
    const lastPos = useRef(position);

    const calcPos = useCallback((e, rect) => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const x = Math.round(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width)  * 100)));
        const y = Math.round(Math.min(100, Math.max(0, ((clientY - rect.top)  / rect.height) * 100)));
        return {x, y};
    }, []);

    const handleStart = useCallback((e) => {
        e.preventDefault();
        isDragging.current = true;
        const rect = containerRef.current.getBoundingClientRect();
        const pos = calcPos(e, rect);
        lastPos.current = pos;
        onChange(pos);
    }, [calcPos, onChange]);

    const handleMove = useCallback((e) => {
        if (!isDragging.current) return;
        e.preventDefault();
        const rect = containerRef.current.getBoundingClientRect();
        const pos = calcPos(e, rect);
        lastPos.current = pos;
        onChange(pos);
    }, [calcPos, onChange]);

    const handleEnd = useCallback(() => {
        if (isDragging.current && onChangeFinal) {
            onChangeFinal(lastPos.current);
        }
        isDragging.current = false;
    }, [onChangeFinal]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        el.addEventListener('mousemove',  handleMove,  {passive:false});
        el.addEventListener('mouseup',    handleEnd);
        el.addEventListener('mouseleave', handleEnd);
        el.addEventListener('touchmove',  handleMove,  {passive:false});
        el.addEventListener('touchend',   handleEnd);
        return () => {
            el.removeEventListener('mousemove',  handleMove);
            el.removeEventListener('mouseup',    handleEnd);
            el.removeEventListener('mouseleave', handleEnd);
            el.removeEventListener('touchmove',  handleMove);
            el.removeEventListener('touchend',   handleEnd);
        };
    }, [handleMove, handleEnd]);

    return (
        <div style={{marginTop:12}}>
            <p style={{fontSize:11, color:'var(--mid)', letterSpacing:1, textTransform:'uppercase', marginBottom:8}}>
                Centrage de la photo — cliquez ou faites glisser la croix
            </p>
            <div
                ref={containerRef}
                onMouseDown={handleStart}
                onTouchStart={handleStart}
                style={{
                    position:'relative', width:'100%', height:300,
                    overflow:'hidden', cursor:'crosshair', userSelect:'none',
                    border:'1px solid var(--border)', background:'#f0e8e0',
                }}
            >
                <img
                    src={photoData}
                    alt="Réglage position"
                    style={{
                        position:'absolute', inset:0,
                        width:'100%', height:'100%',
                        objectFit:'cover',
                        objectPosition:`${position.x}% ${position.y}%`,
                        pointerEvents:'none',
                    }}
                />
                {/* focal point crosshair */}
                <div style={{
                    position:'absolute',
                    left:`${position.x}%`, top:`${position.y}%`,
                    transform:'translate(-50%,-50%)',
                    pointerEvents:'none',
                }}>
                    <div style={{
                        width:24, height:24, borderRadius:'50%',
                        border:'2px solid white',
                        boxShadow:'0 0 0 1px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.4)',
                        background:'rgba(255,255,255,0.25)',
                    }}/>
                </div>
                {/* guide lines */}
                <div style={{position:'absolute', top:0, bottom:0, left:`${position.x}%`, width:1, background:'rgba(255,255,255,0.4)', pointerEvents:'none'}}/>
                <div style={{position:'absolute', left:0, right:0, top:`${position.y}%`, height:1, background:'rgba(255,255,255,0.4)', pointerEvents:'none'}}/>
                <div style={{
                    position:'absolute', bottom:6, right:8,
                    fontSize:10, color:'white', letterSpacing:1,
                    textShadow:'0 1px 3px rgba(0,0,0,0.7)',
                    pointerEvents:'none',
                }}>{position.x}% / {position.y}%</div>
            </div>
        </div>
    );
}

function WeddingForm({cfg, setCfg, onSave, onBack, photoData, onPhoto, title, subtitle, backLabel, saveLabel}) {
    const {photoPosition, savePhotoPosition, savePhotoPositionFinal} = useApp();
    const photoInputId = `photoInput_${title.replace(/\s/g,'')}`;

    return (
        <div id="pageSetup" className="page active">
            <div className="hero">
                <div className="ornament">✦ ✦ ✦</div>
                <h1 className="hero-title">{title}</h1>
                <p className="hero-sub">{subtitle}</p>
            </div>
            <div className="container-sm">
                <div className="card" style={{marginBottom:24}}>
                    <span className="tag-gold" style={{marginBottom:20, display:'inline-block'}}>Compte d'accès</span>
                    <div className="grid-2">
                        <div className="form-group"><label>Identifiant</label>
                            <input type="text" disabled placeholder="ex: sophie-et-julien" value={cfg.username}
                                   style={{backgroundColor: '#f0f0f0', color: '#888', cursor: 'not-allowed'}}
                                   onChange={e => setCfg(c => ({...c, username:e.target.value}))}/></div>
                        <div className="form-group"><label>Mot de passe</label>
                            <input type="text" placeholder="••••••••" value={cfg.password}
                                   autoComplete="new-password"
                                   data-form-type="password"
                                   aria-label="Mot de passe"
                                   onChange={e => setCfg(c => ({...c, password:e.target.value}))}/></div>
                    </div>
                </div>
                <div className="card" style={{marginBottom:24}}>
                    <span className="tag-gold" style={{marginBottom:20, display:'inline-block'}}>Les Époux</span>
                    <div className="grid-2">
                        <div className="form-group"><label>Prénom 1</label>
                            <input type="text" placeholder="ex: Sophie" value={cfg.name1}
                                   onChange={e => { const n1=e.target.value; setCfg(c => ({...c, name1:n1})); }}/></div>
                        <div className="form-group"><label>Prénom 2</label>
                            <input type="text" placeholder="ex: Julien" value={cfg.name2}
                                   onChange={e => { const n2=e.target.value; setCfg(c => ({...c, name2:n2})); }}/></div>
                    </div>
                    <div className="form-group">
                        <label>Photo des époux (optionnel)</label>
                        <div className="photo-upload-area" onClick={() => document.getElementById(photoInputId).click()}>
                            {photoData
                                ? <img src={photoData} className="photo-preview" alt="Preview" style={{display:'block'}}/>
                                : <div id="photoPlaceholder">
                                    <div style={{fontSize:32, color:'var(--gold)', marginBottom:8}}>♥</div>
                                    <p style={{fontSize:13, color:'var(--mid)'}}>Cliquez pour ajouter votre photo</p>
                                    <p style={{fontSize:11, color:'var(--gold)', marginTop:4, letterSpacing:1}}>JPG · PNG · WEBP</p>
                                </div>
                            }
                        </div>
                        <input type="file" id={photoInputId} accept="image/*" style={{display:'none'}} onChange={onPhoto}/>
                        {photoData && (
                            <PhotoPositionPicker
                                photoData={photoData}
                                position={photoPosition}
                                onChange={savePhotoPosition}
                                onChangeFinal={savePhotoPositionFinal}
                            />
                        )}
                    </div>
                </div>
                <div className="card" style={{marginBottom:24}}>
                    <span className="tag-gold" style={{marginBottom:20, display:'inline-block'}}>Détails de la Cérémonie</span>
                    <div className="grid-2">
                        <div className="form-group"><label>Date</label><input type="date" value={cfg.date} onChange={e => setCfg(c => ({...c, date:e.target.value}))}/></div>
                        <div className="form-group"><label>Heure</label><input type="time" value={cfg.time} onChange={e => setCfg(c => ({...c, time:e.target.value}))}/></div>
                    </div>
                    <div className="form-group"><label>Lieu</label><input type="text" placeholder="ex: Château de la Lumière, Paris" value={cfg.venue} onChange={e => setCfg(c => ({...c, venue:e.target.value}))}/></div>
                    <div className="form-group"><label>Adresse</label><input type="text" placeholder="ex: 12 rue des Roses, 75001 Paris" value={cfg.address} onChange={e => setCfg(c => ({...c, address:e.target.value}))}/></div>
                    <div className="form-group"><label>Lieu de la réception (si différent)</label><input type="text" placeholder="ex: Salle des Jardins" value={cfg.reception} onChange={e => setCfg(c => ({...c, reception:e.target.value}))}/></div>
                </div>
                <div className="card" style={{marginBottom:24}}>
                    <span className="tag-gold" style={{marginBottom:20, display:'inline-block'}}>Message Personnel</span>
                    <div className="form-group"><label>Message pour les invités</label><textarea placeholder="ex: Nous avons la joie de vous annoncer notre union..." value={cfg.message} onChange={e => setCfg(c => ({...c, message:e.target.value}))}/></div>
                    <div className="form-group"><label>Date limite de réponse</label><input type="date" value={cfg.rsvpDate} onChange={e => setCfg(c => ({...c, rsvpDate:e.target.value}))}/></div>
                    <div className="form-group"><label>Contact RSVP (email ou téléphone)</label><input type="text" placeholder="ex: mariage@email.com" value={cfg.rsvpContact} onChange={e => setCfg(c => ({...c, rsvpContact:e.target.value}))}/></div>
                </div>
                <div style={{display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap'}}>
                    <button className="btn" onClick={onBack}>{backLabel}</button>
                    <button className="btn btn-primary" onClick={onSave}>{saveLabel}</button>
                </div>
            </div>
        </div>
    );
}

// ══════════════════════════════════
//  PAGE: SETUP  /setup
// ══════════════════════════════════
function SetupPage() {
    const {saveWedding, uploadPhoto, photoData, showToast} = useApp();
    const navigate = useNavigate();
    const [cfg, setCfg] = useState({username:'',password:'',name1:'',name2:'',date:'',time:'',venue:'',address:'',reception:'',message:'',rsvpDate:'',rsvpContact:''});

    const handleSave = async () => {
        if (!cfg.username || !cfg.password || !cfg.name1 || !cfg.name2) { showToast('Veuillez remplir tous les champs obligatoires.','error'); return; }
        try { await saveWedding(cfg); showToast('Mariage configuré !'); }
        catch(e) { showToast('Erreur lors de la sauvegarde.','error'); }
    };
    const handlePhoto = async (e) => {
        const file = e.target.files[0]; if (!file) return;
        try { showToast('Upload en cours…'); await uploadPhoto(file); showToast('Photo enregistrée !'); }
        catch(err) { showToast('Erreur upload : '+err.message,'error'); }
    };

    return (
        <>
            <Nav />
            <WeddingForm cfg={cfg} setCfg={setCfg} onSave={handleSave} onBack={() => navigate('/')}
                         photoData={photoData} onPhoto={handlePhoto}
                         title="Configuration du Mariage" subtitle="Créez votre invitation personnalisée"
                         backLabel="← Retour" saveLabel="Enregistrer le mariage" />
        </>
    );
}

// ══════════════════════════════════
//  PAGE: SETTINGS  /:weddingId/settings
// ══════════════════════════════════
function SettingsPage() {
    const {wedding, saveWedding, uploadPhoto, photoData, showToast} = useApp();
    const navigate = useNavigate();
    const {weddingId} = useParams();
    const [cfg, setCfg] = useState({username:'',password:'',name1:'',name2:'',date:'',time:'',venue:'',address:'',reception:'',message:'',rsvpDate:'',rsvpContact:''});

    useEffect(() => {
        if (wedding) setCfg({
            username:wedding.username||'', password:wedding.password||'',
            name1:wedding.name1||'', name2:wedding.name2||'',
            date:wedding.date||'', time:wedding.time||'',
            venue:wedding.venue||'', address:wedding.address||'',
            reception:wedding.reception||'', message:wedding.message||'',
            rsvpDate:wedding.rsvpDate||'', rsvpContact:wedding.rsvpContact||'',
        });
    }, [wedding]);

    const handleSave = async () => {
        if (!cfg.username || !cfg.password || !cfg.name1 || !cfg.name2) { showToast('Veuillez remplir tous les champs obligatoires.','error'); return; }
        try { await saveWedding(cfg); showToast('Mariage mis à jour !'); }
        catch(e) { showToast('Erreur lors de la sauvegarde.','error'); }
    };
    const handlePhoto = async (e) => {
        const file = e.target.files[0]; if (!file) return;
        try { showToast('Upload en cours…'); await uploadPhoto(file); showToast('Photo enregistrée !'); }
        catch(err) { showToast('Erreur upload : '+err.message,'error'); }
    };

    if (!wedding) return <LoadingScreen />;

    return (
        <>
            <Nav />
            <WeddingForm cfg={cfg} setCfg={setCfg} onSave={handleSave} onBack={() => navigate(`/${wedding?.id}/dashboard`)}
                         photoData={photoData} onPhoto={handlePhoto}
                         title="Modifier le Mariage" subtitle={`${wedding.name1} & ${wedding.name2}`}
                         backLabel="← Dashboard" saveLabel="Enregistrer les modifications" />
        </>
    );
}

// ══════════════════════════════════
//  PAGE: INVITATION  /:weddingId
// ══════════════════════════════════
function InvitationPage() {
    const {weddingId} = useParams();
    const location = useLocation();
    const [w, setW]               = useState(null);
    const [photo, setPhoto]       = useState(null);
    const [loading, setLoading]   = useState(true);
    const [rsvpOpen, setRsvpOpen] = useState(false);
    const [rsvpDoneOpen, setRsvpDoneOpen] = useState(false);
    const [rsvpName, setRsvpName]         = useState('');
    const [rsvpChoice, setRsvpChoice]     = useState('');
    const [rsvpComp, setRsvpComp]         = useState('0');
    const [rsvpMsg, setRsvpMsg]           = useState('');
    const [doneTitle, setDoneTitle]       = useState('');
    const [doneMsg, setDoneMsg]           = useState('');
    const [guestInviteUrl, setGuestInviteUrl] = useState('');
    const [toast, setToast] = useState({msg:'', type:'ok'});
    const [guestData, setGuestData] = useState(null); // pre-loaded guest from gid
    const invRef = useRef(null);

    const showT = (msg, type='ok') => { setToast({msg,type}); setTimeout(()=>setToast({msg:'',type:'ok'}),3200); };

    useEffect(() => {
        async function load() {
            const wd = await dbGetWeddingById(weddingId);
            if (wd) {
                setW(wd);
                setPhoto(getPhotoUrl(wd));

                const params = new URLSearchParams(location.search);
                const gid = params.get('gid');
                if (gid) {
                    try {
                        const rows = await supaFetch(`guests?id=eq.${gid}&wedding_id=eq.${wd.id}&limit=1`);
                        if (rows?.[0]) {
                            const g = rowToGuest(rows[0]);
                            setGuestData(g);
                            setRsvpName(g.name);
                            setRsvpComp(String(g.companions || 0));
                        }
                    } catch(_) {}
                }

                if (params.get('rsvp') === '1') setTimeout(() => setRsvpOpen(true), 700);
            }
            setLoading(false);
        }
        load();
        // eslint-disable-next-line
    }, [weddingId]);

    useEffect(() => {
        const el = invRef.current; if (!el) return;
        const handler = (e) => {
            if (e.target.classList.contains('open-rsvp-btn')) {
                setRsvpMsg(''); setRsvpChoice('');
                setRsvpOpen(true);
            }
        };
        el.addEventListener('click', handler);
        return () => el.removeEventListener('click', handler);
    });

    const submitRsvp = async () => {
        if (!rsvpName.trim()) { showT('Veuillez entrer votre nom','error'); return; }
        if (!rsvpChoice) { showT('Veuillez choisir votre réponse','error'); return; }
        const status = rsvpChoice === 'yes' ? 'confirmed' : 'declined';
        const repliedAt = new Date().toISOString();
        try {
            if (guestData) {
                // Known guest — update directly by ID
                await dbUpdateGuest(guestData.id, {status, companions:parseInt(rsvpComp), message:rsvpMsg, replied_at:repliedAt});
            } else {
                // Unknown guest — search by name or create
                const existing = await supaFetch(`guests?name=ilike.${encodeURIComponent(rsvpName.trim())}&wedding_id=eq.${w.id}&limit=1`);
                if (existing?.length > 0) {
                    await dbUpdateGuest(existing[0].id, {status, companions:parseInt(rsvpComp), message:rsvpMsg, replied_at:repliedAt});
                    window._lastRsvpGuestId = existing[0].id;
                } else {
                    const newId = Date.now();
                    window._lastRsvpGuestId = newId;
                    await dbInsertGuest({id:newId, name:rsvpName.trim(), phone:'', note:'', status, weddingId:w.id, companions:parseInt(rsvpComp), message:rsvpMsg, addedAt:repliedAt, repliedAt});
                }
            }
            setRsvpOpen(false);
            setDoneTitle(status==='confirmed'?'À bientôt ! 🎉':'Merci pour votre réponse');
            setDoneMsg(status==='confirmed'
                ? `Votre présence au mariage de ${w.name1} & ${w.name2} est bien confirmée !`
                : `Votre réponse a bien été prise en compte.`);
            // Build QR URL: use guestData id if known, else the existing guest id from DB lookup
            if (status === 'confirmed') {
                const qrGid = guestData ? guestData.id
                    : (() => { try { return window._lastRsvpGuestId; } catch(_) { return null; } })();
                if (qrGid) setGuestInviteUrl(`${window.location.origin}/${w.id}?gid=${qrGid}&rsvp=1`);
                else setGuestInviteUrl(`${window.location.origin}/${w.id}?rsvp=1`);
            } else {
                setGuestInviteUrl('');
            }
            setRsvpDoneOpen(true);
        } catch(e) { showT("Erreur lors de l'enregistrement.",'error'); }
    };

    const rsvpUrl = `${window.location.origin}/${w?.id || weddingId}?rsvp=1`;
    if (loading) return <LoadingScreen />;

    return (
        <>
            <Nav />
            <div id="pageInvitation" className="page active">
                <div className="hero">
                    <div className="ornament">✦ ✦ ✦</div>
                    <h1 className="hero-title">{w ? `${w.name1} & ${w.name2}` : 'Vous êtes invités'}</h1>
                    <p className="hero-sub">Invitation de Mariage</p>
                </div>
                <div className="container-sm">
                    {w ? <div ref={invRef} dangerouslySetInnerHTML={{__html: buildInvitationHTML(w, photo, false, rsvpUrl, false, w.photo_position || null)}}/>
                        : <div style={{textAlign:'center', padding:'60px 20px'}}>
                            <p className="serif" style={{fontSize:24, fontStyle:'italic', color:'var(--mid)'}}>Invitation introuvable.</p>
                        </div>
                    }
                </div>
            </div>

            <Modal id="rsvpModal" open={rsvpOpen} onClose={() => setRsvpOpen(false)}>
                <div className="modal-box">
                    <button className="modal-close" onClick={() => setRsvpOpen(false)}>✕</button>
                    <div className="modal-header">
                        <div className="ornament" style={{color:'rgba(255,255,255,0.5)'}}>✦ ✦ ✦</div>
                        <h2 className="serif" style={{fontSize:28, fontWeight:300, fontStyle:'italic', color:'white'}}>
                            {guestData ? `Bonjour, ${guestData.name.split(' ')[0]} !` : 'Confirmer votre présence'}
                        </h2>
                        {w && <p style={{color:'rgba(255,255,255,0.75)', fontSize:12, letterSpacing:1, marginTop:8}}>au mariage de {w.name1} & {w.name2}</p>}
                    </div>
                    <div className="modal-body">
                        {guestData ? (
                            <div style={{background:'var(--rose-light)', padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:10}}>
                                <span style={{fontSize:20}}>👤</span>
                                <div>
                                    <p style={{fontWeight:500, color:'var(--rose-dark)', fontSize:14}}>{guestData.name}</p>
                                    {guestData.tableNumber && <p style={{fontSize:12, color:'var(--mid)'}}>🪑 Table N° {guestData.tableNumber}</p>}
                                </div>
                            </div>
                        ) : (
                            <div className="form-group"><label>Votre prénom et nom</label>
                                <input type="text" placeholder="ex: Marie Dupont" value={rsvpName} onChange={e => setRsvpName(e.target.value)}/></div>
                        )}
                        <div className="form-group"><label>Votre réponse</label>
                            <div style={{display:'flex', gap:12, marginTop:4}}>
                                <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', textTransform:'none', letterSpacing:0, fontSize:14, color:'var(--dark)'}}>
                                    <input type="radio" name="rsvpChoice" value="yes" style={{width:'auto'}} checked={rsvpChoice==='yes'} onChange={() => setRsvpChoice('yes')}/> Avec joie ! ✓
                                </label>
                                <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', textTransform:'none', letterSpacing:0, fontSize:14, color:'var(--dark)'}}>
                                    <input type="radio" name="rsvpChoice" value="no" style={{width:'auto'}} checked={rsvpChoice==='no'} onChange={() => setRsvpChoice('no')}/> Je ne pourrai pas être présent(e)
                                </label>
                            </div>
                        </div>
                        <div className="form-group"><label>Nombre d'accompagnateurs</label>
                            <select value={rsvpComp} onChange={e => setRsvpComp(e.target.value)}>
                                <option value="0">Seul(e)</option>
                                <option value="1">+1 accompagnateur</option>
                                <option value="2">+2 accompagnateurs</option>
                                <option value="3">+3 accompagnateurs</option>
                                <option value="4">+4 accompagnateurs ou plus</option>
                            </select>
                        </div>
                        <div className="form-group"><label>Message pour les mariés (optionnel)</label>
                            <textarea placeholder="Un mot pour les heureux époux..." value={rsvpMsg} onChange={e => setRsvpMsg(e.target.value)}/></div>
                        <button className="btn btn-primary" style={{width:'100%'}} onClick={submitRsvp}>Envoyer ma réponse</button>
                    </div>
                </div>
            </Modal>

            <Modal id="rsvpDoneModal" open={rsvpDoneOpen} onClose={() => setRsvpDoneOpen(false)}>
                <div className="modal-box">
                    <div className="modal-header" style={{background: guestInviteUrl ? 'linear-gradient(135deg,var(--sage),#5a8a5a)' : 'linear-gradient(135deg,var(--mid),#4a3a35)'}}>
                        <div style={{fontSize:40, marginBottom:12}}>{guestInviteUrl ? '♥' : '🙏'}</div>
                        <h2 className="serif" style={{fontSize:28, fontWeight:300, fontStyle:'italic', color:'white'}}>{doneTitle}</h2>
                    </div>
                    <div className="modal-body" style={{textAlign:'center'}}>
                        <p className="serif" style={{fontSize:18, fontStyle:'italic', color:'var(--mid)', lineHeight:1.7}}>{doneMsg}</p>

                        {/* QR Code — shown only when confirmed */}
                        {guestInviteUrl && (
                            <div style={{margin:'24px 0'}}>
                                <div className="ornament" style={{marginBottom:16}}>✦ ✦ ✦</div>
                                <p style={{fontSize:11, letterSpacing:2, textTransform:'uppercase', color:'var(--mid)', marginBottom:16}}>
                                    Votre code d'entrée
                                </p>
                                <div style={{display:'inline-block', padding:16, background:'var(--cream)', border:'1px solid var(--border)'}}>
                                    <GuestQRCode url={guestInviteUrl} size={180} />
                                </div>
                                <p style={{fontSize:11, color:'var(--mid)', marginTop:12, lineHeight:1.7}}>
                                    Présentez ce QR code à l'entrée de l'événement.<br/>
                                    <span style={{color:'var(--gold)'}}>Faites une capture d'écran pour le conserver.</span>
                                </p>
                            </div>
                        )}

                        {!guestInviteUrl && <div className="ornament" style={{margin:'24px 0'}}>✦</div>}
                        <button className="btn btn-primary" onClick={() => setRsvpDoneOpen(false)}>Fermer</button>
                    </div>
                </div>
            </Modal>
            <Toast message={toast.msg} type={toast.type}/>
        </>
    );
}

// ══════════════════════════════════
//  PAGE: PREVIEW  /:weddingId/preview
// ══════════════════════════════════
function PreviewPage() {
    const {wedding, photoData, photoPosition} = useApp();
    const navigate = useNavigate();
    const invRef = useRef(null);
    const [rsvpOpen, setRsvpOpen]         = useState(false);
    const [rsvpName, setRsvpName]         = useState('');
    const [rsvpChoice, setRsvpChoice]     = useState('');
    const [rsvpComp, setRsvpComp]         = useState('0');
    const [rsvpMsg, setRsvpMsg]           = useState('');

    const rsvpUrl = `${window.location.origin}/${wedding?.id}?rsvp=1`;

    useEffect(() => {
        const el = invRef.current; if (!el) return;
        const handler = (e) => {
            if (e.target.classList.contains('open-rsvp-btn')) {
                setRsvpName(''); setRsvpMsg(''); setRsvpChoice(''); setRsvpComp('0');
                setRsvpOpen(true);
            }
        };
        el.addEventListener('click', handler);
        return () => el.removeEventListener('click', handler);
    });

    if (!wedding) return <LoadingScreen />;

    return (
        <>
            <Nav />
            <div className="page active">
                {/* top bar */}
                <div style={{background:'white', borderBottom:'1px solid var(--border)', padding:'12px 32px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12}}>
                    <button
                        className="btn"
                        style={{padding:'8px 20px', fontSize:12}}
                        onClick={() => navigate(`/${wedding.id}/dashboard`)}>
                        ← Retour au tableau de bord
                    </button>
                    <div className="nav-logo" style={{fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', color:'var(--rose-dark)', fontSize:18}}>
                        Aperçu de l'invitation
                    </div>
                    <button
                        className="btn btn-primary"
                        style={{padding:'8px 20px', fontSize:12}}
                        onClick={() => navigate(`/${wedding.id}/settings`)}>
                        ✎ Modifier
                    </button>
                </div>

                {/* invitation rendered full-width, centered */}
                <div style={{background:'var(--cream)', minHeight:'calc(100vh - 57px)', padding:'40px 20px'}}>
                    <div ref={invRef}
                         dangerouslySetInnerHTML={{__html: buildInvitationHTML(wedding, photoData, false, rsvpUrl, false, photoPosition)}}
                         style={{maxWidth:480, margin:'0 auto'}}/>
                </div>
            </div>

            <Modal id="rsvpPreviewModal" open={rsvpOpen} onClose={() => setRsvpOpen(false)}>
                <div className="modal-box">
                    <button className="modal-close" onClick={() => setRsvpOpen(false)}>✕</button>
                    <div className="modal-header">
                        <div className="ornament" style={{color:'rgba(255,255,255,0.5)'}}>✦ ✦ ✦</div>
                        <h2 className="serif" style={{fontSize:28, fontWeight:300, fontStyle:'italic', color:'white'}}>Confirmer votre présence</h2>
                        {wedding && <p style={{color:'rgba(255,255,255,0.75)', fontSize:12, letterSpacing:1, marginTop:8}}>au mariage de {wedding.name1} & {wedding.name2}</p>}
                    </div>
                    <div className="modal-body">
                        <div className="form-group"><label>Votre prénom et nom</label>
                            <input type="text" placeholder="ex: Marie Dupont" value={rsvpName} onChange={e => setRsvpName(e.target.value)}/></div>
                        <div className="form-group"><label>Votre réponse</label>
                            <div style={{display:'flex', gap:12, marginTop:4}}>
                                <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', textTransform:'none', letterSpacing:0, fontSize:14, color:'var(--dark)'}}>
                                    <input type="radio" name="rsvpChoicePrev" value="yes" style={{width:'auto'}} checked={rsvpChoice==='yes'} onChange={() => setRsvpChoice('yes')}/> Avec joie ! ✓
                                </label>
                                <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', textTransform:'none', letterSpacing:0, fontSize:14, color:'var(--dark)'}}>
                                    <input type="radio" name="rsvpChoicePrev" value="no" style={{width:'auto'}} checked={rsvpChoice==='no'} onChange={() => setRsvpChoice('no')}/> Je ne pourrai pas être présent(e)
                                </label>
                            </div>
                        </div>
                        <div className="form-group"><label>Nombre d'accompagnateurs</label>
                            <select value={rsvpComp} onChange={e => setRsvpComp(e.target.value)}>
                                <option value="0">Seul(e)</option>
                                <option value="1">+1 accompagnateur</option>
                                <option value="2">+2 accompagnateurs</option>
                                <option value="3">+3 accompagnateurs</option>
                                <option value="4">+4 accompagnateurs ou plus</option>
                            </select>
                        </div>
                        <div className="form-group"><label>Message pour les mariés (optionnel)</label>
                            <textarea placeholder="Un mot pour les heureux époux..." value={rsvpMsg} onChange={e => setRsvpMsg(e.target.value)}/></div>
                        <p style={{fontSize:12, color:'var(--gold)', textAlign:'center', marginTop:8}}>
                            ✦ Aperçu uniquement — les réponses ne sont pas enregistrées ici
                        </p>
                    </div>
                </div>
            </Modal>
        </>
    );
}

// ══════════════════════════════════
//  PHONE COUNTRY SELECTOR
// ══════════════════════════════════
const COUNTRIES = [
    { code: 'cd', dial: '+243', name: 'Congo (RDC)' },
    { code: 'cg', dial: '+242', name: 'Congo (Brazzaville)' },
    { code: 'fr', dial: '+33',  name: 'France' },
    { code: 'be', dial: '+32',  name: 'Belgique' },
    { code: 'ch', dial: '+41',  name: 'Suisse' },
    { code: 'ci', dial: '+225', name: "Côte d'Ivoire" },
    { code: 'cm', dial: '+237', name: 'Cameroun' },
    { code: 'sn', dial: '+221', name: 'Sénégal' },
    { code: 'ml', dial: '+223', name: 'Mali' },
    { code: 'ga', dial: '+241', name: 'Gabon' },
    { code: 'ao', dial: '+244', name: 'Angola' },
    { code: 'rw', dial: '+250', name: 'Rwanda' },
    { code: 'bi', dial: '+257', name: 'Burundi' },
    { code: 'ke', dial: '+254', name: 'Kenya' },
    { code: 'tz', dial: '+255', name: 'Tanzanie' },
    { code: 'za', dial: '+27',  name: 'Afrique du Sud' },
    { code: 'ng', dial: '+234', name: 'Nigeria' },
    { code: 'gh', dial: '+233', name: 'Ghana' },
    { code: 'ma', dial: '+212', name: 'Maroc' },
    { code: 'dz', dial: '+213', name: 'Algérie' },
    { code: 'tn', dial: '+216', name: 'Tunisie' },
    { code: 'gb', dial: '+44',  name: 'Royaume-Uni' },
    { code: 'it', dial: '+39',  name: 'Italie' },
    { code: 'pt', dial: '+351', name: 'Portugal' },
    { code: 'us', dial: '+1',   name: 'États-Unis' },
    { code: 'ca', dial: '+1',   name: 'Canada' },
];
function FlagImg({ code, size = 20 }) {
    return (
        <img
            src={`https://flagcdn.com/w40/${code}.png`}
            alt={code.toUpperCase()}
            width={size}
            height={Math.round(size * 0.75)}
            style={{ display:'block', borderRadius:2, objectFit:'cover', flexShrink:0 }}
        />
    );
}
const DEFAULT_COUNTRY = COUNTRIES[0]; // +243 Congo RDC

function PhoneInput({ value, onChange }) {
    // value is the full phone string e.g. "+243812345678"
    // We split it into dialCode + local number on mount/change
    const [dropOpen, setDropOpen] = useState(false);
    const [country, setCountry]   = useState(() => {
        const match = COUNTRIES.find(c => value && value.startsWith(c.dial));
        return match || DEFAULT_COUNTRY;
    });
    const [localNum, setLocalNum] = useState(() => {
        const match = COUNTRIES.find(c => value && value.startsWith(c.dial));
        return match ? value.slice(match.dial.length).trimStart() : (value || '');
    });
    const dropRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const selectCountry = (c) => {
        setCountry(c);
        setDropOpen(false);
        onChange(localNum ? `${c.dial}${localNum}` : '');
    };

    const handleLocalChange = (e) => {
        // Strip any leading zeros or spaces
        const raw = e.target.value.replace(/[^\d\s\-]/g, '');
        setLocalNum(raw);
        onChange(raw ? `${country.dial}${raw}` : '');
    };

    return (
        <div style={{display:'flex', position:'relative'}} ref={dropRef}>
            {/* Flag button */}
            <button
                type="button"
                onClick={() => setDropOpen(v => !v)}
                style={{
                    display:'flex', alignItems:'center', justifyContent:'center',
                    gap:4, padding:'0 10px', height:40,
                    border:'1px solid var(--border)', borderRight:'none',
                    borderRadius:'4px 0 0 4px', background:'var(--light)',
                    cursor:'pointer', flexShrink:0, fontSize:20, lineHeight:1,
                    minWidth:52,
                }}
                title={`${country.name} (${country.dial})`}
            >
                <FlagImg code={country.code} size={22} />
                <span style={{fontSize:9, color:'var(--mid)', marginTop:1}}>▾</span>
            </button>

            {/* Number input */}
            <input
                type="tel"
                placeholder="ex: 812 345 678"
                value={localNum}
                onChange={handleLocalChange}
                style={{
                    flex:1, border:'1px solid var(--border)',
                    borderRadius:'0 4px 4px 0', padding:'0 12px', height:40,
                    fontSize:14, outline:'none', background:'white',
                    fontFamily:'inherit', color:'var(--dark)',
                    minWidth:0,
                }}
            />

            {/* Dropdown */}
            {dropOpen && (
                <div style={{
                    position:'absolute', top:'100%', left:0, zIndex:200,
                    background:'white', border:'1px solid var(--border)',
                    borderRadius:6, boxShadow:'0 8px 32px rgba(0,0,0,0.12)',
                    maxHeight:260, overflowY:'auto', minWidth:230,
                    marginTop:2,
                }}>
                    {COUNTRIES.map(c => (
                        <div
                            key={c.code + c.dial}
                            onClick={() => selectCountry(c)}
                            style={{
                                display:'flex', alignItems:'center', gap:10,
                                padding:'9px 14px', cursor:'pointer', fontSize:13,
                                background: c.code === country.code ? 'var(--light)' : 'white',
                                color:'var(--dark)',
                                transition:'background 0.1s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--light)'}
                            onMouseLeave={e => e.currentTarget.style.background = c.code === country.code ? 'var(--light)' : 'white'}
                        >
                            <FlagImg code={c.code} size={18} />
                            <span style={{flex:1}}>{c.name}</span>
                            <span style={{color:'var(--mid)', fontSize:12, fontVariantNumeric:'tabular-nums'}}>{c.dial}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════
//  GUEST FORM (top-level to prevent re-mount on mobile)
// ══════════════════════════════════
function GuestForm({guestName, setGuestName, guestPhone, setGuestPhone, guestNote, setGuestNote, guestTable, setGuestTable, isCouple, setIsCouple, handleAdd}) {
    return (
        <>
            <div className="form-group"><label>Prénom et Nom</label>
                <input type="text" placeholder="ex: Marie Dupont" value={guestName} onChange={e => setGuestName(e.target.value)}/></div>
            {/*<div className="form-group">*/}
            {/*    <label>Téléphone (WhatsApp)</label>*/}
            {/*    <PhoneInput value={guestPhone} onChange={setGuestPhone} />*/}
            {/*</div>*/}
            <div className="grid-2">
                <div className="form-group"><label>Table (optionnel)</label>
                    <input type="text" placeholder="ex: 5" value={guestTable} onChange={e => setGuestTable(e.target.value)}/></div>
                <div className="form-group"><label>Note (optionnel)</label>
                    <input type="text" placeholder="ex: Cousine de Sophie" value={guestNote} onChange={e => setGuestNote(e.target.value)}/></div>
            </div>
            <label style={{display:'flex', alignItems:'center', gap:10, cursor:'pointer', margin:'8px 0 12px', fontSize:13, color:'var(--dark)', userSelect:'none'}}>
                <div onClick={() => setIsCouple(v => !v)}
                     style={{width:40, height:22, borderRadius:11, background:isCouple?'var(--gold)':'#ddd', position:'relative', transition:'background 0.2s', flexShrink:0, cursor:'pointer'}}>
                    <div style={{width:18, height:18, borderRadius:'50%', background:'white', position:'absolute', top:2, left:isCouple?20:2, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)'}}/>
                </div>
                Couple
            </label>
            <button className="btn btn-primary" style={{width:'100%', marginTop:8}} onClick={handleAdd}>Ajouter l'invité</button>
        </>
    );
}

// ══════════════════════════════════
//  PAGE: DASHBOARD  /:weddingId/dashboard
// ══════════════════════════════════
function DashboardPage() {
    const {wedding, guests, photoData, addGuest, removeGuest, showToast, setGuests} = useApp();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [guestName, setGuestName]   = useState('');
    const [guestPhone, setGuestPhone] = useState('');
    const [guestNote, setGuestNote]   = useState('');
    const [guestTable, setGuestTable] = useState('');
    const [isCouple, setIsCouple]     = useState(false);
    const [addGuestOpen, setAddGuestOpen] = useState(false);
    const [search, setSearch]         = useState('');
    const [filter, setFilter]         = useState('all');
    const [filterOpen, setFilterOpen]   = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [dbSearchResults, setDbSearchResults] = useState(null); // null = no db search done
    const [dbSearching, setDbSearching] = useState(false);
    const PAGE_SIZE = 10;

    const confirmed = guests.filter(g => g.status==='confirmed').length;
    const declined  = guests.filter(g => g.status==='declined').length;
    const pending   = guests.filter(g => g.status==='pending').length;
    const totalPax  = guests.filter(g => g.status==='confirmed').reduce((s,g) => s+1+(parseInt(g.companions)||0), 0);

    const generatePDF = useCallback(async () => {
        // Dynamically load jsPDF + autoTable if needed
        if (!window.jspdf) {
            await new Promise((res, rej) => {
                const s = document.createElement('script');
                s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
                s.onload = res; s.onerror = rej;
                document.head.appendChild(s);
            });
        }
        if (!window.jspdf?.jsPDF?.API?.autoTable) {
            await new Promise((res, rej) => {
                const s = document.createElement('script');
                s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
                s.onload = res; s.onerror = rej;
                document.head.appendChild(s);
            });
        }

        const { jsPDF } = window.jspdf;
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const W = doc.internal.pageSize.getWidth();

        // ── Palette ──────────────────────────────────────────────────────────
        const roseDark = [139, 94, 74];
        const gold     = [184, 152, 90];
        const mid      = [107,  92, 85];
        const light    = [245, 237, 232];

        // ── Header band ──────────────────────────────────────────────────────
        doc.setFillColor(...roseDark);
        doc.rect(0, 0, W, 38, 'F');

        // Ornament — thin gold line with dots (no unicode)
        doc.setDrawColor(...gold);
        doc.setLineWidth(0.3);
        const ox = W / 2;
        doc.line(ox - 30, 9, ox - 6, 9);
        doc.line(ox + 6,  9, ox + 30, 9);
        doc.setFillColor(...gold);
        doc.circle(ox, 9, 1, 'F');
        doc.circle(ox - 4, 9, 0.5, 'F');
        doc.circle(ox + 4, 9, 0.5, 'F');

        // Names
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bolditalic');
        doc.setTextColor(255, 255, 255);
        doc.text(`${wedding.name1} & ${wedding.name2}`, W / 2, 22, { align: 'center' });

        // Date · venue
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(255, 220, 200);
        const sub = [wedding.date ? formatDate(wedding.date) : '', wedding.venue || ''].filter(Boolean).join('   |   ');
        doc.text(sub.toUpperCase(), W / 2, 31, { align: 'center' });

        // Separator line
        doc.setDrawColor(...gold);
        doc.setLineWidth(0.4);
        doc.line(14, 35, W - 14, 35);

        // ── Summary cards row ────────────────────────────────────────────────
        const stats = [
            { label: 'Total invites', value: guests.reduce((s,g) => s+1+(parseInt(g.companions)||0), 0) },
            { label: 'Confirmes',     value: confirmed },
            { label: 'Declines',      value: declined },
            { label: 'En attente',    value: pending },
            { label: 'Personnes',     value: totalPax },
        ];
        const cardW = (W - 28 - (stats.length - 1) * 3) / stats.length;
        stats.forEach((st, i) => {
            const x = 14 + i * (cardW + 3);
            doc.setFillColor(...light);
            doc.roundedRect(x, 41, cardW, 18, 2, 2, 'F');
            doc.setTextColor(...roseDark);
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text(String(st.value), x + cardW / 2, 51, { align: 'center' });
            doc.setFontSize(6.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...mid);
            doc.text(st.label.toUpperCase(), x + cardW / 2, 56, { align: 'center' });
        });

        // ── Section title ────────────────────────────────────────────────────
        doc.setTextColor(...roseDark);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bolditalic');
        doc.text('Liste des invites', 14, 70);
        doc.setDrawColor(...gold);
        doc.setLineWidth(0.3);
        doc.line(14, 72, W - 14, 72);

        // ── Table ────────────────────────────────────────────────────────────
        const statusLabel = s => s === 'confirmed' ? 'Confirmé' : s === 'declined' ? 'Décliné' : 'En attente';

        doc.autoTable({
            startY: 75,
            margin: { left: 14, right: 14 },
            head: [['#', 'Nom', 'Telephone', 'Statut', 'Table', 'Acc.', 'Note']],
            body: guests.map((g, i) => [
                i + 1,
                g.name + (g.companions === 1 ? ' +1' : ''),
                g.phone || '-',
                g.status === 'confirmed' ? 'Confirme' : g.status === 'declined' ? 'Decline' : 'En attente',
                g.tableNumber ? `N ${g.tableNumber}` : '-',
                g.companions || 0,
                g.note || '-',
            ]),
            styles: {
                fontSize: 8.5,
                cellPadding: 3,
                font: 'helvetica',
                textColor: [60, 45, 40],
                lineColor: [212, 180, 131],
                lineWidth: 0.2,
            },
            headStyles: {
                fillColor: roseDark,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: 8,
                halign: 'left',
            },
            alternateRowStyles: { fillColor: [250, 247, 242] },
            columnStyles: {
                0: { cellWidth: 8,  halign: 'center' },
                3: { cellWidth: 22 },
                4: { cellWidth: 14, halign: 'center' },
                5: { cellWidth: 10, halign: 'center' },
            },
        });

        // ── Footer ───────────────────────────────────────────────────────────
        const pageCount = doc.internal.getNumberOfPages();
        for (let p = 1; p <= pageCount; p++) {
            doc.setPage(p);
            doc.setFontSize(7);
            doc.setTextColor(...mid);
            doc.text(`Promessa  |  ${guests.length} invites  |  ${confirmed} confirmes  |  ${declined} declines`, 14, 292);
            doc.text(`${p} / ${pageCount}`, W - 14, 292, { align: 'right' });
        }

        doc.save(`invites_${wedding.name1}_${wedding.name2}.pdf`);
    }, [guests, wedding, confirmed, declined, pending, totalPax]);

    const matchesSearch = useCallback((g, q) =>
            g.name.toLowerCase().includes(q)
            || (g.phone && g.phone.includes(q))
            || (g.note && g.note.toLowerCase().includes(q))
            || (g.tableNumber && String(g.tableNumber).includes(q))
        , []);

    // Base filtered list (by status filter + local search)
    const statusFiltered = guests.filter(g => filter==='all' || g.status===filter);
    const localSearched = search.trim()
        ? statusFiltered.filter(g => matchesSearch(g, search.toLowerCase()))
        : statusFiltered;

    // When search or filter changes: reset page and clear any previous DB results
    useEffect(() => {
        setCurrentPage(1);
        setDbSearchResults(null);
    }, [search, filter]);

    // If local search yields nothing, try DB after debounce
    useEffect(() => {
        if (!search.trim()) return;
        if (localSearched.length > 0) return;        // local results found — no DB call needed
        if (dbSearchResults !== null) return;        // already did a DB search for this query

        const timer = setTimeout(async () => {
            if (!wedding?.id) return;
            setDbSearching(true);
            try {
                const q = encodeURIComponent(search.trim());
                const rows = await supaFetch(
                    `guests?wedding_id=eq.${wedding.id}&or=(name.ilike.*${q}*,phone.ilike.*${q}*,note.ilike.*${q}*,table_number.ilike.*${q}*)&select=*&order=added_at.asc`
                );
                setDbSearchResults((rows || []).map(rowToGuest));
            } catch(e) {
                showToast('Erreur lors de la recherche.', 'error');
            } finally {
                setDbSearching(false);
            }
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [search, localSearched.length]);

    // Final display list: prefer DB results when local search was empty
    const displayList = dbSearchResults !== null ? dbSearchResults : localSearched;

    // Pagination
    const totalPages = Math.max(1, Math.ceil(displayList.length / PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const paginated = displayList.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const rsvpUrl = `${window.location.origin}/${wedding?.id}?rsvp=1`;

    const handleAdd = async () => {
        const name = guestName.trim();
        if (!name) { showToast("Veuillez entrer le nom de l'invité",'error'); return; }
        const ok = await addGuest(name, guestPhone.trim(), guestNote.trim(), guestTable.trim(), isCouple);
        if (ok) {
            setGuestName(''); setGuestPhone(''); setGuestNote(''); setGuestTable(''); setIsCouple(false);
            showToast(`${name} ajouté(e) !`);
            if (isMobile) setAddGuestOpen(false);
        }
    };

    const sendWhatsApp = (g) => {
        //if (!g.phone) { showToast("Pas de numéro WhatsApp pour cet invité.",'warn'); return; }
        const inviteUrl = `${window.location.origin}/${wedding.id}?gid=${g.id}&rsvp=1&_t=${Date.now()}`;
        const dateStr = wedding.date ? formatDate(wedding.date) : 'Date à confirmer';
        const timeStr = wedding.time ? ` \u00e0 ${wedding.time.replace(':','h')}` : '';
        const venueStr = wedding.venue ? `\n\uD83D\uDCCD ${wedding.venue}${wedding.address ? ', ' + wedding.address : ''}` : '';
        const receptionStr = wedding.reception ? `\n\uD83C\uDF7E R\u00e9ception : ${wedding.reception}` : '';
        const tableStr = g.tableNumber ? `\n\uD83C\uDF7F Votre table : N\u00b0 ${g.tableNumber}` : '';
        const companionStr = g.companions === 1 ? `\n\uD83D\uDC6B Vous \u00eates invit\u00e9(e) avec votre accompagnateur(trice)` : '';
        const rsvpDeadline = wedding.rsvpDate
            ? `\n\n\u2705 Merci de confirmer votre pr\u00e9sence avant le ${formatDate(wedding.rsvpDate)} :`
            : `\n\n\u2705 Confirmez votre pr\u00e9sence :`;

        const msg = `\uD83D\uDC8C *Invitation de Mariage*\n\nCh\u00e8r(e) ${g.name},\n\nNous avons la joie de vous convier \u00e0 la c\u00e9l\u00e9bration de notre mariage \uD83D\uDC8D\n\n\uD83D\uDC92 *${wedding.name1} & ${wedding.name2}*\n\uD83D\uDCC5 ${dateStr}${timeStr}${venueStr}${receptionStr}${tableStr}${companionStr}\n\nNous esp\u00e9rons vous compter parmi nous en ce jour si pr\u00e9cieux. \uD83C\uDF38${rsvpDeadline}\n\n${inviteUrl}`;

        const clean = g.phone.replace(/^\+/, '').replace(/\s/g, '');
        window.open(
            `https://api.whatsapp.com/send/?phone=${clean}&text=${encodeURIComponent(msg)}&type=phone_number&app_absent=0`,
            '_blank'
        );
    };

    if (!wedding) return <LoadingScreen />;

    // GuestForm is defined outside DashboardPage (see below) to avoid re-mounting on every keystroke

    return (
        <>
            <Nav />
            <div id="pageDashboard" className="page active">
                <div className="hero">
                    <div className="ornament">✦ ✦ ✦</div>
                    <h1 className="hero-title">{wedding.name1} & {wedding.name2}</h1>
                    <p className="hero-sub">{wedding.date?formatDate(wedding.date):''}{wedding.venue?` · ${wedding.venue}`:''}</p>
                    <button
                        className="btn btn-sage"
                        style={{marginTop:20, padding:'10px 28px', fontSize:13, letterSpacing:1}}
                        onClick={() => navigate(`/${wedding.id}/preview`)}>
                        👁 Aperçu de l'invitation
                    </button>
                </div>
                <div className="container">
                    {/* STATS */}
                    {isMobile ? (
                        <div style={{display:'flex', marginBottom:20, background:'white', border:'1px solid var(--border)', borderRadius:8, overflow:'hidden'}}>
                            {[
                                {num: guests.reduce((s,g) => s+1+(parseInt(g.companions)||0), 0), label:'Total',      color:'var(--rose-dark)'},
                                {num: confirmed,     label:'Confirmés',  color:'var(--sage)'},
                                {num: declined,      label:'Déclinés',   color:'#c0392b'},
                            ].map((s,i,arr) => (
                                <div key={i} style={{flex:1, textAlign:'center', padding:'12px 8px', borderRight: i < arr.length-1 ? '1px solid var(--border)' : 'none'}}>
                                    <div style={{fontSize:22, fontWeight:600, color:s.color, lineHeight:1}}>{s.num}</div>
                                    <div style={{fontSize:10, letterSpacing:0.5, textTransform:'uppercase', color:'var(--mid)', marginTop:4}}>{s.label}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{display:'flex', gap:12, marginBottom:28}}>
                            {[
                                {num: guests.reduce((s,g) => s+1+(parseInt(g.companions)||0), 0), label:'Invités total', color:'var(--rose-dark)'},
                                {num: confirmed,      label:'Confirmés',    color:'var(--sage)'},
                                {num: declined,       label:'Déclinés',     color:'#c0392b'},
                                {num: pending,        label:'En attente',   color:'var(--gold)'},
                            ].map((s,i) => (
                                <div key={i} className="stat-card" style={{flex:1}}>
                                    <span className="stat-num" style={{color:s.color}}>{s.num}</span>
                                    <div className="stat-label">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={{display: isMobile ? 'block' : 'grid', gridTemplateColumns:'1fr 1fr', gap:40, alignItems:'start'}}>
                        {/* LEFT — desktop only inline form */}
                        {!isMobile && (
                            <div>
                                <h3 className="serif" style={{fontSize:22, fontWeight:400, marginBottom:20, color:'var(--rose-dark)'}}>Inviter un invité</h3>
                                <div className="card">
                                    <GuestForm guestName={guestName} setGuestName={setGuestName} guestPhone={guestPhone} setGuestPhone={setGuestPhone} guestNote={guestNote} setGuestNote={setGuestNote} guestTable={guestTable} setGuestTable={setGuestTable} isCouple={isCouple} setIsCouple={setIsCouple} handleAdd={handleAdd}/>
                                </div>
                            </div>
                        )}

                        {/* RIGHT — guest list */}
                        <div>
                            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: isMobile ? 0 : 20, flexWrap:'wrap', gap:12}}>
                                <h3 className="serif" style={{fontSize:22, fontWeight:400, color:'var(--rose-dark)'}}>Liste des invités</h3>
                                {!isMobile && (
                                    <div style={{display:'flex', gap:8}}>
                                        <button className="btn btn-sage" onClick={generatePDF} style={{padding:'8px 16px', fontSize:11}}>Imprimer PDF</button>
                                        <button className="btn btn-primary" onClick={() => navigate(`/${wedding.id}/checkin`)} style={{padding:'8px 16px', fontSize:11}}>
                                            <CameraIcon size={13} style={{marginRight:5}}/> Scanner QR
                                        </button>
                                    </div>
                                )}
                            </div>
                            {isMobile && (
                                <div style={{display:'flex', gap:8, marginTop:12, marginBottom:20}}>
                                    <button className="btn btn-primary" style={{flex:1, padding:'10px 0', fontSize:11}}
                                            onClick={() => setAddGuestOpen(true)}>
                                        + Ajouter
                                    </button>
                                    <button className="btn btn-sage" onClick={generatePDF} style={{flex:1, padding:'10px 0', fontSize:11}}>Imprimer PDF</button>
                                    <button className="btn btn-primary" onClick={() => navigate(`/${wedding.id}/checkin`)} style={{flex:1, padding:'10px 0', fontSize:11}}><CameraIcon size={13} style={{marginRight:4}}/> Scanner</button>
                                </div>
                            )}

                            {/* ── Filter row: inline on desktop, collapsible on mobile ── */}
                            {isMobile ? (
                                <div style={{marginBottom:16}}>
                                    <button
                                        onClick={() => setFilterOpen(v => !v)}
                                        style={{
                                            display:'flex', alignItems:'center', gap:8,
                                            background:'white', border:'1px solid var(--border)',
                                            padding:'8px 14px', cursor:'pointer', width:'100%',
                                            fontFamily:"'Jost',sans-serif", fontSize:11,
                                            letterSpacing:1.5, textTransform:'uppercase', color:'var(--mid)',
                                        }}>
                                        {/* Funnel icon */}
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                                        </svg>
                                        Filtrer
                                        <span style={{marginLeft:'auto', fontSize:10, background:'var(--rose-light)', color:'var(--rose-dark)', padding:'2px 8px', borderRadius:2}}>
                                            {{all:'Tous', confirmed:'Confirmés', declined:'Déclinés', pending:'En attente'}[filter]}
                                        </span>
                                        <span style={{fontSize:12, color:'var(--mid)', marginLeft:4}}>{filterOpen ? '▲' : '▼'}</span>
                                    </button>
                                    {filterOpen && (
                                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginTop:6}}>
                                            {['all','confirmed','declined','pending'].map(f => (
                                                <button key={f} className={`filter-btn${filter===f?' active':''}`}
                                                        onClick={() => { setFilter(f); setFilterOpen(false); }}
                                                        style={{textAlign:'center', padding:'9px 0'}}>
                                                    {{all:'Tous', confirmed:'Confirmés', declined:'Déclinés', pending:'En attente'}[f]}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{display:'flex', gap:8, marginBottom:16}}>
                                    {['all','confirmed','declined','pending'].map(f => (
                                        <button key={f} className={`filter-btn${filter===f?' active':''}`}
                                                onClick={() => setFilter(f)} style={{flex:1, textAlign:'center'}}>
                                            {{all:'Tous', confirmed:'Confirmés', declined:'Déclinés', pending:'En attente'}[f]}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div style={{marginBottom:16, position:'relative'}}>
                                <span style={{position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none'}}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                    </svg>
                                </span>
                                <input type="text" placeholder="Rechercher…"
                                       value={search} onChange={e => setSearch(e.target.value)}
                                       style={{width:'100%', paddingLeft:36, boxSizing:'border-box'}}/>
                            </div>

                            <div className="card" style={{padding:0, overflow:'hidden'}}>
                                {dbSearching && (
                                    <div style={{padding:'10px 20px', fontSize:12, color:'var(--gold)', borderBottom:'1px solid var(--border)'}}>
                                        🔍 Recherche dans la base de données…
                                    </div>
                                )}
                                {displayList.length === 0
                                    ? <div style={{padding:40, textAlign:'center'}}>
                                        <p style={{fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontStyle:'italic', color:'var(--mid)'}}>
                                            {search ? 'Aucun résultat trouvé' : 'Aucun invité pour le moment'}
                                        </p>
                                        <p style={{fontSize:12, color:'var(--mid)', marginTop:8}}>
                                            {search ? `Aucun invité ne correspond à "${search}".` : 'Ajoutez vos premiers invités.'}
                                        </p>
                                    </div>
                                    : <>
                                        <div className="table-wrap">
                                            <table>
                                                <thead><tr>
                                                    <th>Invité</th>
                                                    <th>Statut</th>
                                                    {!isMobile && <th>Table</th>}
                                                    {!isMobile && <th>Note</th>}
                                                    <th></th><th></th>
                                                </tr></thead>
                                                <tbody>
                                                {paginated.map(g => (
                                                    <tr key={g.id} style={g.checkedIn ? {background:'#e8f5e9', opacity:0.85, transition:'background 0.3s'} : {}}>
                                                        <td>
                                                            <span style={{fontSize: isMobile ? 13 : 14, color: g.checkedIn ? '#2e7d32' : 'inherit'}}>
                                                                <strong>{g.name}</strong>
                                                            </span>
                                                            {isMobile && g.tableNumber && <div style={{fontSize:11, color: g.checkedIn ? '#388e3c' : 'var(--rose-dark)', marginTop:2}}>Table N°{g.tableNumber}</div>}
                                                            {isMobile && (g.note || g.companions===1) && (
                                                                <div style={{fontSize:11, color:'var(--mid)', marginTop:1, display:'flex', alignItems:'center', gap:3}}>
                                                                    {g.companions===1 && <span style={{color: g.checkedIn ? '#388e3c' : 'var(--gold)', display:'inline-flex', alignItems:'center'}}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="6" r="3" fill="currentColor"/><path d="M2 19c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none"/><circle cx="17" cy="6" r="2.5" fill="currentColor" opacity=".7"/><path d="M13.5 19c0-2.761 1.567-5 3.5-5s3.5 2.239 3.5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity=".7"/></svg></span>}
                                                                    {g.note && <span>{g.note}</span>}
                                                                </div>
                                                            )}
                                                            {isMobile && g.phone && <div style={{fontSize:11, color:'var(--mid)', marginTop:1}}>{g.phone}</div>}
                                                        </td>
                                                        <td><StatusBadge status={g.status} checkedIn={g.checkedIn}/></td>
                                                        {!isMobile && <td style={{textAlign:'center', fontWeight:600, color: g.checkedIn ? '#388e3c' : 'var(--rose-dark)'}}>{g.tableNumber?`N°${g.tableNumber}`:'—'}</td>}
                                                        {!isMobile && <td style={{color:'var(--mid)', fontSize:12}}>
                                                            <span style={{display:'inline-flex', alignItems:'center', gap:4}}>
                                                                {g.companions===1 && <span style={{color: g.checkedIn ? '#388e3c' : 'var(--gold)', display:'inline-flex', alignItems:'center', flexShrink:0}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="6" r="3" fill="currentColor"/><path d="M2 19c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none"/><circle cx="17" cy="6" r="2.5" fill="currentColor" opacity=".7"/><path d="M13.5 19c0-2.761 1.567-5 3.5-5s3.5 2.239 3.5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity=".7"/></svg></span>}
                                                                <span>{g.note||(!g.companions ? '—' : '')}</span>
                                                            </span>
                                                        </td>}
                                                        <td>
                                                            <button title={g.checkedIn ? 'Déjà présent' : 'Envoyer via WhatsApp'}
                                                                    disabled={g.checkedIn}
                                                                    style={{background:'none', border:'none', cursor: g.checkedIn ? 'not-allowed' : 'pointer', padding:'2px 4px', color: g.checkedIn ? '#b0c4b0' : '#25D366', opacity: g.checkedIn ? 0.4 : 1, transition:'opacity 0.2s'}}
                                                                    onClick={() => !g.checkedIn && sendWhatsApp(g)}>
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                                </svg>
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <button className="btn btn-danger" style={{padding:'4px 10px', fontSize:10, opacity: g.checkedIn ? 0.3 : 1, cursor: g.checkedIn ? 'not-allowed' : 'pointer', transition:'opacity 0.2s'}}
                                                                    disabled={g.checkedIn}
                                                                    onClick={() => !g.checkedIn && removeGuest(g.id)}>✕</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {totalPages > 1 && (
                                            <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px 16px', borderTop:'1px solid var(--border)', flexWrap:'wrap'}}>
                                                <button className="btn" style={{padding:'4px 12px', fontSize:12}}
                                                        disabled={safePage <= 1}
                                                        onClick={() => setCurrentPage(p => Math.max(1, p-1))}>‹ Préc.</button>
                                                {Array.from({length: totalPages}, (_,i) => i+1).map(p => (
                                                    <button key={p}
                                                            className={`filter-btn${p===safePage?' active':''}`}
                                                            style={{minWidth:32, padding:'4px 8px', fontSize:12}}
                                                            onClick={() => setCurrentPage(p)}>{p}</button>
                                                ))}
                                                <button className="btn" style={{padding:'4px 12px', fontSize:12}}
                                                        disabled={safePage >= totalPages}
                                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))}>Suiv. ›</button>
                                                <span style={{fontSize:11, color:'var(--mid)', marginLeft:4}}>
                                                    {(safePage-1)*PAGE_SIZE+1}–{Math.min(safePage*PAGE_SIZE, displayList.length)} / {displayList.length}
                                                </span>
                                            </div>
                                        )}
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* MOBILE ADD GUEST MODAL */}
            <Modal id="addGuestModal" open={addGuestOpen} onClose={() => setAddGuestOpen(false)}>
                <div className="modal-box">
                    <button className="modal-close" onClick={() => setAddGuestOpen(false)}>✕</button>
                    <div className="modal-header">
                        <div className="ornament" style={{color:'rgba(255,255,255,0.5)', fontSize:14}}>✦ ✦ ✦</div>
                        <h2 className="serif" style={{fontSize:26, fontWeight:300, fontStyle:'italic', color:'white'}}>Ajouter un invité</h2>
                    </div>
                    <div className="modal-body">
                        <GuestForm guestName={guestName} setGuestName={setGuestName} guestPhone={guestPhone} setGuestPhone={setGuestPhone} guestNote={guestNote} setGuestNote={setGuestNote} guestTable={guestTable} setGuestTable={setGuestTable} isCouple={isCouple} setIsCouple={setIsCouple} handleAdd={handleAdd}/>
                    </div>
                </div>
            </Modal>

            {/* PRINT AREA */}
            <div id="printArea" style={{display:'none'}}>
                <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif"}}>
                    <h1 style={{textAlign:'center', fontSize:'28pt', fontWeight:300, fontStyle:'italic', color:'#8b5e4a', marginBottom:'8pt'}}>Liste des invités</h1>
                    <h2 style={{textAlign:'center', fontSize:'16pt', fontWeight:300, color:'#6b5c55'}}>{wedding.name1} & {wedding.name2}</h2>
                    <p style={{textAlign:'center', fontSize:'12pt', color:'#6b5c55', marginBottom:'20pt'}}>
                        {wedding.date?formatDate(wedding.date):''}{wedding.venue?` · ${wedding.venue}`:''}
                    </p>
                    <table style={{width:'100%', borderCollapse:'collapse', fontFamily:"'Jost',sans-serif", fontSize:'10pt'}}>
                        <thead><tr style={{background:'#f5ede8'}}>
                            {['#','Prénom & Nom','Statut','Table','Accomp.','Note'].map(h=>(
                                <th key={h} style={{padding:'8pt 10pt', textAlign:'left', border:'0.5pt solid #d4b483'}}>{h}</th>
                            ))}
                        </tr></thead>
                        <tbody>
                        {guests.map((g,i) => (
                            <tr key={g.id} style={{background:i%2===0?'white':'#faf7f2'}}>
                                <td style={{padding:'6pt 10pt', border:'0.5pt solid #d4b483'}}>{i+1}</td>
                                <td style={{padding:'6pt 10pt', border:'0.5pt solid #d4b483'}}>{g.name}{g.companions===1?' (couple)':''}</td>
                                <td style={{padding:'6pt 10pt', border:'0.5pt solid #d4b483'}}>{g.phone||'—'}</td>
                                <td style={{padding:'6pt 10pt', border:'0.5pt solid #d4b483'}}>{g.status==='confirmed'?'Confirmé ✓':g.status==='declined'?'Décliné':'En attente'}</td>
                                <td style={{padding:'6pt 10pt', border:'0.5pt solid #d4b483', textAlign:'center', fontWeight:'bold'}}>{g.tableNumber||'—'}</td>
                                <td style={{padding:'6pt 10pt', border:'0.5pt solid #d4b483', textAlign:'center'}}>{g.companions||0}</td>
                                <td style={{padding:'6pt 10pt', border:'0.5pt solid #d4b483'}}>{g.note||'—'}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <div style={{marginTop:'16pt', fontSize:'10pt'}}>
                        <strong>Résumé :</strong> {guests.length} invités — {confirmed} confirmés ({totalPax} personnes) — {declined} déclinés — {pending} en attente.
                    </div>
                </div>
            </div>
        </>
    );
}